/**
 * English: Flows and chatbots
 *
 * What this file covers: The chatbot flow builder, its steps, AI answers, Build with AI, and the chatbots
 * themselves.
 *
 * Tone: Practical and a little technical, never heavy with jargon. Step names are short actions (Ask a
 * question, Book a time). Explanations say what the step does for the client.
 *
 * English: English is the reference: every key is added here first, in South African English (organise, colour,
 * cancelled). Every other language falls back to this text for any key it does not have yet.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const flows: Catalog = {
  'flows.title': 'Flows',
  'flows.actions.deleteEllipsis': 'Delete flow…',
  'flows.view.runs.total': 'Runs',
  'flows.view.runs.median': 'Median time',
  'flows.view.runs.failedOnly': 'Failed only',
  'flows.view.runs.error': 'The runs could not be loaded. This is not the same as having none.',
  'flows.view.runs.noFailures': 'Nothing has failed',
  'flows.view.runs.emptyHint': 'Runs appear here once someone sends a message that matches the trigger.',
  'flows.view.runs.trigger': 'What they said',
  'flows.view.runs.reply': 'What the flow replied',
  'flows.view.runs.reason': 'Reason',
  'flows.view.runs.started': 'Started',
  'flows.view.runs.duration': 'Took',
  'flows.view.runs.detail': 'Run detail',
  'flows.view.runs.timing': 'Timing',
  'flows.view.runs.stillRunning': 'still running',
  'flows.view.runs.confidence': '{{value}}% confident',
  'flows.view.runs.citations': 'Grounded on',
  'flows.view.runs.citation': 'Passage {{chunk}}',
  'flows.runStatus.completed': 'Completed',
  'flows.runStatus.failed': 'Failed',
  'flows.runStatus.running': 'Running',
  'flows.runStatus.handed_off': 'Handed to an agent',
  'flows.actions.for': 'Actions for {{name}}',
  'flows.actions.editJson': 'Edit as JSON',
  'flows.columns.health': 'Recent runs',
  'flows.search.label': 'Search flows',
  'flows.search.placeholder': 'Search name or description',
  'flows.empty.hint': 'A flow answers people automatically when a message matches its trigger.',
  'flows.emptyFiltered': 'No flows match',
  'flows.drawer.new': 'New flow',
  'flows.drawer.edit': 'Edit flow',
  'flows.drawer.descriptionHint': 'For your team. Customers never see it.',
  'flows.health.neverRun': 'Never run',
  'flows.health.okTooltip': 'Last {{total}} runs all completed.',
  'flows.health.failedTooltip': '{{failed}} of the last {{total}} runs failed.',
  'flows.toast.deleted': '{{name}} deleted.',
  'flows.toast.deleteFailed': 'Delete failed',
  'flows.toast.activated': '{{name}} is live and will start replying.',
  'flows.toast.paused': '{{name}} paused. It will not reply to anyone.',
  'flows.toast.toggleFailed': 'Could not change the flow',
  'flows.modal.activate.title': 'Activate {{name}}?',
  'flows.modal.activate.body': 'This flow will start replying to customers automatically as soon as a message matches its trigger.',
  'flows.modal.activate.hint': 'You can pause it again at any time; runs already under way finish.',
  'flows.modal.delete.confirmNamed': 'Delete {{name}}?',
  'flows.modal.delete.inFlight': 'Runs already under way finish; the flow stops triggering. This cannot be undone.',
  'flows.validate.noTrigger': 'This flow has no trigger, so nothing will ever start it.',
  'flows.validate.unlinked': 'Some steps are not linked to anything, so they will never run.',
  'flows.validate.invalidJson': 'The definition is not valid JSON.',
  'flows.builder.title': 'Builder',
  'flows.builder.open': 'Open in builder',
  'flows.builder.undo': 'Undo',
  'flows.builder.redo': 'Redo',
  'flows.builder.showNode': 'Show me',
  'flows.builder.leave.title': 'Leave without saving?',
  'flows.builder.leave.body': 'You have unsaved changes to this flow. They will be lost.',
  'flows.builder.leave.discard': 'Leave anyway',
  'flows.builder.leave.keep': 'Stay here',
  'flows.builder.discard.title': 'Discard your changes?',
  'flows.builder.discard.body': 'The canvas goes back to the last saved version. Undo cannot bring these changes back.',
  'flows.builder.discard.confirm': 'Discard them',
  'flows.builder.discard.keep': 'Keep editing',
  'flows.builder.json.discard.title': 'Discard your JSON edits?',
  'flows.builder.json.discard.body': 'You changed the definition here and did not press Apply, so the canvas has not seen it.',
  'flows.builder.json.discard.confirm': 'Discard them',
  'flows.builder.json.discard.keep': 'Stay in JSON',
  'flows.builder.field.keywords': 'Keywords',
  'flows.builder.field.keywordsHint': 'Separate several with commas. Any one of them starts the flow.',
  'flows.builder.field.kbIdsHint2': 'The AI answers from the documents you tick.',
  'flows.builder.field.kbIdsUnavailable': 'Your knowledge base could not be loaded, so ids are shown raw. One per line.',
  'flows.builder.field.kbEmpty': 'No documents in the knowledge base yet.',
  'flows.builder.field.kbUnknown': '{{id}} is selected but no longer in your library.',
  'flows.validate.unlinkedNode': '{{id}} is not linked to anything, so it will never run. Link it from another step or remove it.',
  'flows.validate.noReply': 'Nothing in this flow replies, so the customer hears nothing.',
  'flows.validate.llmNoPrompt': '{{id}} has no instructions, so the AI has nothing to work with.',
  'flows.validate.handoffThreshold': '{{id}} has a threshold of {{value}}; it must be between 0 and 1.',
  'flows.builder.back': 'Back to flow',
  'flows.builder.discard': 'Discard',
  'flows.builder.saved': 'Flow definition saved.',
  'flows.builder.saveFailed': 'Save failed',
  'flows.builder.unsaved': 'Unsaved changes',
  'flows.builder.help': 'Click the + under a node to add the next step, or drag a node from the palette. Drag from a node\'s bottom handle onto another node to link them, or onto empty canvas to add a node there. Scroll to move around, Ctrl + scroll to zoom.',
  'flows.builder.canvasTab': 'Canvas',
  'flows.builder.advancedTab': 'Advanced (JSON)',
  'flows.builder.canvas': 'Flow canvas',
  'flows.builder.palette': 'Nodes',
  'flows.builder.properties': 'Properties',
  'flows.builder.selectNode': 'Select a node to edit its properties.',
  'flows.builder.unlinked': 'Not linked',
  'flows.builder.unlinkedHint': 'A step that is not linked from another step never runs. Drag from a port onto it, or remove it.',
  'flows.builder.noTrigger': 'This flow has no trigger, so it never fires. Add one from the palette.',
  'flows.builder.passthrough': 'pass-through',
  'flows.builder.applyJson': 'Apply JSON',
  'flows.builder.jsonApplied': 'Definition applied to the canvas.',
  'flows.builder.invalidJson': 'Must be valid JSON.',
  'flows.builder.jsonObjectRequired': 'The definition must be a JSON object.',
  'flows.builder.advancedHint': 'The exact JSON the flow runner executes. Apply to update the canvas; Save to persist.',
  'flows.builder.connect.self': 'A node cannot link to itself.',
  'flows.builder.connect.intoTrigger': 'Nothing can link into the trigger.',
  'flows.builder.connect.cycle': 'That link would create a loop.',
  'flows.builder.connect.unknown': 'Unknown node.',
  'flows.builder.group.start': 'Start',
  'flows.builder.group.steps': 'Steps',
  'flows.builder.node.trigger': 'Trigger',
  'flows.builder.node.triggerHint': 'An incoming WhatsApp message starts the flow',
  'flows.builder.node.llm': 'Reply with AI',
  'flows.builder.node.llmHint': 'Answer with AI, from your knowledge base',
  'flows.builder.node.reply': 'Send reply',
  'flows.builder.node.replyHint': 'Send a message (defaults to the last AI answer)',
  'flows.builder.node.handoff': 'Hand off',
  'flows.builder.node.handoffHint': 'Give the conversation to a person',
  'flows.builder.node.raw': 'Unrecognised entry',
  'flows.builder.summary.always': 'Every inbound message',
  'flows.builder.summary.keyword': 'Keyword',
  'flows.builder.summary.keywords': 'Keywords (engine reads `keyword`)',
  'flows.builder.summary.noKeyword': 'No keyword set',
  'flows.builder.summary.lastAnswer': 'The last AI answer',
  'flows.builder.summary.confidence': 'confidence',
  'flows.builder.summary.human': 'human',
  'flows.builder.summary.noFields': 'No fields',
  'flows.builder.allFields': 'All fields (JSON)',
  'flows.builder.genericHint': 'The engine does not interpret this node kind; its fields are kept exactly as written.',
  'flows.builder.rawHint': 'This entry is not an object with a `type`; it is stored verbatim.',
  'flows.builder.rawValue': 'Value (JSON)',
  'flows.builder.field.triggerType': 'Fires on',
  'flows.builder.field.keywordMatch': 'Keyword match',
  'flows.builder.field.always': 'Every inbound message',
  'flows.builder.field.keyword': 'Keyword',
  'flows.builder.field.keywordHint': 'Case-insensitive substring of the inbound text.',
  'flows.builder.field.promptCode': 'Prompt code',
  'flows.builder.field.promptCodeHint': 'Named prompt the model runs with (e.g. smart_reply).',
  'flows.builder.field.kbIds': 'Knowledge-base document ids',
  'flows.builder.field.kbIdsHint': 'One id per line; the reply is grounded on these documents.',
  'flows.builder.field.body': 'Message',
  'flows.builder.field.bodyHint': 'Leave empty to send the last AI answer.',
  'flows.builder.field.threshold': 'Confidence threshold',
  'flows.builder.field.thresholdHint': 'Hands over when the last AI answer\'s confidence is below this (0 to 1).',
  'flows.builder.field.reason': 'Reason (for your team)',
  'flows.toast.saved': 'Flow {{name}} saved.',
  'flows.view.lifecycle.inactiveHint': 'This flow is inactive: incoming events won\'t trigger it.',
  'flows.view.lifecycle.toast.activated': 'Flow is now active.',
  'flows.view.lifecycle.toast.deactivated': 'Flow is now inactive.',
  'flows.view.lifecycle.toast.deleted': 'Deleted {{name}}.',
  'flows.builder.flowNotFound': 'Flow not found.',
  'flows.description': 'Chatbots that answer on their own. A flow is the conversation a bot follows: greet, ask, answer with AI, and hand over to a person when it should.',
  'flows.drawer.activeHint': 'Active flows run on matching inbound messages.',
  'flows.drawer.definition': 'Definition',
  'flows.drawer.definitionHint': 'trigger: { type: "keyword" | "always", keywords?: [] } · nodes: ordered list of { id, type } where type is llm, reply or handoff.',
  'flows.drawer.definitionInvalid': 'Definition must be valid JSON.',
  'flows.drawer.nameRequired': 'Name is required.',
  'flows.empty': 'No flows yet: create one to automate replies.',
  'flows.loadError': 'Failed to load flows.',
  'flows.modal.delete.confirm': 'Delete flow',
  'flows.modal.delete.title': 'Delete flow?',
  'flows.runs.empty': 'No runs yet for this flow.',
  'flows.view.backToAll': 'All flows',
  'flows.view.definition.copied': 'Flow definition copied.',
  'flows.view.definition.hint': 'Raw definition executed by the flow runner. Edit it visually with {{open}} above: the builder round-trips this JSON losslessly.',
  'flows.view.lifecycle.activateFlow': 'Activate flow',
  'flows.view.lifecycle.activation': 'Activation',
  'flows.view.lifecycle.activeHint': 'This flow is active and will run when its trigger fires.',
  'flows.view.lifecycle.deactivateFlow': 'Deactivate flow',
  'flows.view.lifecycle.deleteFailed': 'Failed to delete flow.',
  'flows.view.lifecycle.deleteHint': 'Permanently removes this flow. Existing runs are preserved for audit but no new runs will be triggered.',
  'flows.view.lifecycle.deleteWarning': 'This action is permanent and cannot be undone.',
  'flows.view.lifecycle.updateFailed': 'Failed to update flow.',
  'flows.view.overview.flowId': 'Flow ID',
  'flows.view.overview.title': 'Flow details',
  'flows.view.runs.columns.contact': 'Contact',
  'flows.view.runs.columns.duration': 'Duration',
  'flows.view.runs.columns.error': 'Error',
  'flows.view.runs.liveHint': 'Live: refreshes every 10s.',
  'flows.view.runs.title': 'Recent runs',
  'flows.view.tabs.runs': 'Runs',
  'flows.view.updatedPrefix': '· updated',
  'flows.banner': 'Flows run on their own in the background whenever their trigger fires.',
  'flows.actions.create': 'Create flow',
  'flows.filters.status.any': 'All statuses ({{count}})',
  'flows.showingOf': 'Showing {{count}} of {{total}} flows',
  'flows.view.summary.title': 'What this flow does',
  'flows.view.summary.empty': 'This flow has no steps yet. Open it in the builder to add some.',
  'flows.view.summary.hint': '{{count}} step(s), run in this order every time the trigger fires.',
  'flows.view.runs.viewAll': 'View all runs',
  'flows.view.idCopied': 'Flow ID copied.',
  'flows.node.trigger': 'Trigger',
  'flows.node.llm': 'Answer with AI',
  'flows.node.reply': 'Send reply',
  'flows.node.handoff': 'Hand off',
  'flows.builder.save': 'Save changes',
  'flows.builder.group.triggers': 'Triggers',
  'flows.builder.group.actions': 'Actions',
  'flows.builder.group.logic': 'Logic',
  'flows.builder.node.send': 'Send message',
  'flows.builder.node.sendHint': 'Text, media, buttons or a list',
  'flows.builder.node.ask': 'Ask a question',
  'flows.builder.node.askHint': 'Wait for the answer and store it',
  'flows.builder.node.http': 'HTTP request',
  'flows.builder.node.httpHint': 'Call an external API and use the response',
  'flows.builder.node.contact': 'Update contact',
  'flows.builder.node.contactHint': 'Write what was collected to the contact',
  'flows.builder.node.set': 'Set variable',
  'flows.builder.node.setHint': 'Store data in a variable',
  'flows.builder.node.condition': 'Condition',
  'flows.builder.node.conditionHint': 'Branch on what you know',
  'flows.builder.node.delay': 'Delay',
  'flows.builder.node.delayHint': 'Wait before continuing',
  'flows.builder.node.end': 'End',
  'flows.builder.node.endHint': 'Stop the flow, with an optional last message',
  'flows.builder.searchNodes': 'Search nodes',
  'flows.builder.paletteHint': 'Drag a node onto the canvas, or click it to add it after the selected node.',
  'flows.builder.test': 'Test flow',
  'flows.builder.tidy': 'Tidy up',
  'flows.builder.removeNode': 'Remove node',
  'flows.builder.stats': '{{nodes}} nodes · {{problems}} to fix',
  'flows.builder.moreProblems': '{{count}} more',
  'flows.builder.status.ok': 'Ready',
  'flows.builder.status.check': 'Needs attention',
  'flows.builder.connect.unknownPort': 'That node has no such output.',
  'flows.builder.port.yes': 'Yes',
  'flows.builder.port.no': 'No',
  'flows.builder.port.success': 'Success',
  'flows.builder.port.error': 'Error',
  'flows.builder.port.invalid': 'No match',
  'flows.builder.summary.anyMessage': 'Every incoming message',
  'flows.builder.summary.webhook': 'Started from your system',
  'flows.builder.summary.media': 'Sends a file',
  'flows.builder.summary.noQuestion': 'No question yet',
  'flows.builder.summary.llm': '{{prompt}} · {{kb}} documents',
  'flows.builder.summary.nothingToUpdate': 'Nothing to update yet',
  'flows.builder.summary.handoffAlways': 'Always hands to a person',
  'flows.builder.summary.handoffThreshold': 'Hands over below confidence {{value}}',
  'flows.builder.summary.nothingToSet': 'Nothing to set yet',
  'flows.builder.summary.noRules': 'No rules yet',
  'flows.builder.summary.noDelay': 'No delay set',
  'flows.builder.summary.delaySeconds': 'Waits {{n}} seconds',
  'flows.builder.summary.delayMinutes': 'Waits {{n}} minutes',
  'flows.builder.summary.delayHours': 'Waits {{n}} hours',
  'flows.builder.summary.delayDays': 'Waits {{n}} days',
  'flows.builder.summary.endSilent': 'Ends without a message',
  'flows.builder.field.name': 'Node name',
  'flows.builder.field.nameHint': 'What this step is, in your words. Shown on the canvas.',
  'flows.builder.field.description': 'Description',
  'flows.builder.field.descriptionHint': 'What this step does and why. For you and your team.',
  'flows.builder.field.templateHint': 'Use {{variable}} to insert a value. Available:',
  'flows.builder.field.triggerTypeHint': 'Keywords start the flow only when a message contains one. "Every message" catches everything that no keyword flow claimed.',
  'flows.builder.field.anyMessage': 'Every incoming message',
  'flows.builder.field.webhook': 'From your system (API)',
  'flows.builder.field.webhookHint': 'Start this flow for a contact by calling POST /v1/flows/{id}/trigger, for example when an order is placed.',
  'flows.builder.field.keywordMode': 'Match',
  'flows.builder.field.keywordContains': 'Message contains the keyword',
  'flows.builder.field.keywordExact': 'Message is exactly the keyword',
  'flows.builder.field.keywordStarts': 'Message starts with the keyword',
  'flows.builder.field.channelsHint': 'Runs on WhatsApp and on your website chat. Messenger and Instagram arrive with their adapters.',
  'flows.builder.field.question': 'Question',
  'flows.builder.field.variable': 'Save the answer as',
  'flows.builder.field.variableHint': 'A variable name: letters, digits and underscores. Use it later as {{name}}.',
  'flows.builder.field.options': 'Choices',
  'flows.builder.field.optionsHint': 'Up to three show as buttons; more show as a list. Each choice becomes its own branch.',
  'flows.builder.field.optionsListHint': 'More than three choices: WhatsApp shows them as a list.',
  'flows.builder.field.optionTitle': 'Choice',
  'flows.builder.field.optionId': 'Id (optional)',
  'flows.builder.field.addOption': 'Add a choice',
  'flows.builder.field.validate': 'Accept only',
  'flows.builder.field.validateHint': 'Anything else is asked again, using the retry message.',
  'flows.builder.field.pattern': 'Pattern (regular expression)',
  'flows.builder.field.retryMessage': 'Retry message',
  'flows.builder.field.retryMessageHint': 'Sent when the answer is not accepted. Leave blank to repeat the question.',
  'flows.builder.field.maxRetries': 'Retries before giving up',
  'flows.builder.field.media': 'Attach a file',
  'flows.builder.field.mediaHint': 'An image, video or PDF sent with the message.',
  'flows.builder.field.caption': 'Caption',
  'flows.builder.field.systemPrompt': 'Instructions for the AI',
  'flows.builder.field.systemPromptHint': 'Who the assistant is and how it should answer. Leave blank for the default assistant.',
  'flows.builder.field.input': 'What to ask about',
  'flows.builder.field.saveAs': 'Save the result as',
  'flows.builder.field.saveAsLlmHint': 'The answer is then {{llm.text}} and its confidence {{llm.confidence}}.',
  'flows.builder.field.saveAsHttpHint': 'The response is then {{http.body}}, with {{http.status}} and {{http.ok}}.',
  'flows.builder.field.method': 'Method',
  'flows.builder.field.url': 'URL',
  'flows.builder.field.headers': 'Headers',
  'flows.builder.field.headerName': 'Header',
  'flows.builder.field.headerValue': 'Value',
  'flows.builder.field.addHeader': 'Add a header',
  'flows.builder.field.contentType': 'Body format',
  'flows.builder.field.form': 'Form fields',
  'flows.builder.field.text': 'Plain text',
  'flows.builder.field.requestBody': 'Request body',
  'flows.builder.field.requestBodyHint': 'JSON or text. Variables are filled in before sending.',
  'flows.builder.field.extract': 'Pick values from the response',
  'flows.builder.field.extractHint': 'A variable name and a path into the response, such as data.items[0].status.',
  'flows.builder.field.path': 'Path',
  'flows.builder.field.addExtract': 'Add a value',
  'flows.builder.field.timeout': 'Timeout (ms)',
  'flows.builder.field.contactName': 'Contact name',
  'flows.builder.field.contactEmail': 'Contact email',
  'flows.builder.field.customFields': 'Custom fields',
  'flows.builder.field.customFieldsHint': 'Stored on the contact and available in later flows as {{contact.fields.name}}.',
  'flows.builder.field.fieldName': 'Field',
  'flows.builder.field.fieldValue': 'Value',
  'flows.builder.field.addField': 'Add a field',
  'flows.builder.field.addTags': 'Add tags',
  'flows.builder.field.tagsHint': 'Comma-separated. Tags are the groups broadcasts can target.',
  'flows.builder.field.removeTags': 'Remove tags',
  'flows.builder.field.handoffAlways': 'Always hand over',
  'flows.builder.field.handoffAlwaysHint': 'Off: hand over only when the last AI answer was below the confidence threshold.',
  'flows.builder.field.handoffMessage': 'Message to the customer',
  'flows.builder.field.handoffMessageHint': 'Sent as the conversation goes to a person.',
  'flows.builder.field.assignments': 'Variables',
  'flows.builder.field.value': 'Value',
  'flows.builder.field.addAssignment': 'Add a variable',
  'flows.builder.field.match': 'Branch to Yes when',
  'flows.builder.field.matchAll': 'All rules are true',
  'flows.builder.field.matchAny': 'Any rule is true',
  'flows.builder.field.rules': 'Rules',
  'flows.builder.field.ruleLeft': 'Variable',
  'flows.builder.field.ruleOp': 'Operator',
  'flows.builder.field.ruleRight': 'Value',
  'flows.builder.field.addRule': 'Add a rule',
  'flows.builder.field.conditionPorts': 'Link the Yes and No outputs to what should happen next. An unlinked output ends the flow.',
  'flows.builder.field.days': 'Days',
  'flows.builder.field.hours': 'Hours',
  'flows.builder.field.minutes': 'Minutes',
  'flows.builder.field.seconds': 'Seconds',
  'flows.builder.field.delayHint': 'Up to seven days. A new message from the customer during the wait starts a fresh run instead.',
  'flows.builder.field.endMessage': 'Last message',
  'flows.builder.op.eq': 'equals',
  'flows.builder.op.neq': 'does not equal',
  'flows.builder.op.contains': 'contains',
  'flows.builder.op.not_contains': 'does not contain',
  'flows.builder.op.starts_with': 'starts with',
  'flows.builder.op.ends_with': 'ends with',
  'flows.builder.op.gt': 'is greater than',
  'flows.builder.op.gte': 'is at least',
  'flows.builder.op.lt': 'is less than',
  'flows.builder.op.lte': 'is at most',
  'flows.builder.op.in': 'is one of (comma-separated)',
  'flows.builder.op.exists': 'is set',
  'flows.builder.op.not_exists': 'is not set',
  'flows.builder.op.empty': 'is empty',
  'flows.builder.op.not_empty': 'is not empty',
  'flows.builder.op.matches': 'matches pattern',
  'flows.builder.validate.none': 'Anything',
  'flows.builder.validate.not_empty': 'A non-empty answer',
  'flows.builder.validate.number': 'A number',
  'flows.builder.validate.email': 'An email address',
  'flows.builder.validate.phone': 'A phone number',
  'flows.builder.validate.regex': 'A pattern',
  'flows.validate.triggerNoKeyword': 'The trigger has no keyword, so nothing will start this flow.',
  'flows.validate.noBranchLinked': '{{id}} has outputs but none of them leads anywhere.',
  'flows.validate.tooManyOptions': '{{id}} offers more than ten choices; WhatsApp allows ten.',
  'flows.validate.askNoQuestion': '{{id}} asks nothing: add the question.',
  'flows.validate.askNoVariable': '{{id}} does not save the answer: give it a variable name.',
  'flows.validate.badVariableName': '"{{name}}" is not a valid variable name: use letters, digits and underscores.',
  'flows.validate.httpNoUrl': '{{id}} has no URL to call.',
  'flows.validate.httpBadUrl': '{{id}} needs an absolute http or https URL.',
  'flows.validate.contactNothing': '{{id}} updates nothing on the contact.',
  'flows.validate.setNothing': '{{id}} sets no variables.',
  'flows.validate.conditionNoRules': '{{id}} has no rules, so it always says Yes.',
  'flows.validate.delayZero': '{{id}} waits for no time at all.',
  'flows.validate.delayTooLong': '{{id}} waits longer than seven days, the most a run may sleep.',
  'flows.test.title': 'Test: {{name}}',
  'flows.test.hint': 'Try the flow as a customer would. Nothing is sent to anyone and nothing is billed; the canvas is what runs, saved or not.',
  'flows.test.empty': 'Send the first message to start, or let the trigger keyword start it.',
  'flows.test.start': 'Start with the trigger keyword',
  'flows.test.thinking': 'Working',
  'flows.test.failed': 'The flow failed. See the steps below.',
  'flows.test.handedOff': 'The flow handed the conversation to a person.',
  'flows.test.ended': 'The flow ended.',
  'flows.test.restart': 'Start again',
  'flows.test.send': 'Send',
  'flows.test.placeholder': 'Type a message',
  'flows.test.placeholderAnswer': 'Type your answer',
  'flows.test.placeholderFirst': 'What the customer says first',
  'flows.test.state': '{{state}} · at {{node}}',
  'flows.test.states.running': 'Running',
  'flows.test.states.waiting_input': 'Waiting for an answer',
  'flows.test.states.waiting_delay': 'Waiting (delay)',
  'flows.test.states.done': 'Ended',
  'flows.test.showState': 'Show variables and steps',
  'flows.test.hideState': 'Hide variables and steps',
  'flows.test.variables': 'Variables',
  'flows.test.steps': 'Steps',
  'flows.node.send': 'Send message',
  'flows.node.ask': 'Ask a question',
  'flows.node.http': 'HTTP request',
  'flows.node.contact': 'Update contact',
  'flows.node.set': 'Set variable',
  'flows.node.condition': 'Condition',
  'flows.node.delay': 'Delay',
  'flows.node.end': 'End',
  'flows.starter.keyword': 'hi',
  'flows.starter.greetingName': 'Greeting',
  'flows.starter.greeting': 'Hi {{contact.name ?? there}}! Welcome.',
  'flows.starter.askName': 'What do they need?',
  'flows.starter.question': 'How can we help today?',
  'flows.starter.optionOrder': 'An order',
  'flows.starter.optionPerson': 'Talk to someone',
  'flows.starter.orderName': 'Order help',
  'flows.starter.orderHelp': 'Sure. Tell us your order number and we will look it up.',
  'flows.starter.handoffName': 'Hand over',
  'flows.starter.handoff': 'One moment, a colleague will be with you.',
  'flows.view.runs.steps': 'Steps',
  'flows.view.runs.variables': 'Variables collected',
  'flows.view.runs.waiting': '{{state}}, at "{{node}}". The next message from the customer continues it.',
  'flows.builder.quickAdd.title': 'Add a node',
  'flows.builder.quickAdd.hint': 'It is linked from the point you clicked.',
  'flows.builder.quickAdd.search': 'Type to filter',
  'flows.builder.quickAdd.empty': 'No node matches.',
  'flows.builder.nodeMenu': 'Node actions',
  'flows.builder.duplicate': 'Duplicate',
  'flows.builder.canvasEmpty': 'Click Add a step, double-click the canvas, or click the + under the trigger to add the first step.',
  'flows.builder.canvasControls.zoomIn': 'Zoom in',
  'flows.builder.canvasControls.zoomOut': 'Zoom out',
  'flows.builder.canvasControls.reset': 'Actual size',
  'flows.builder.canvasControls.fit': 'Fit to view',
  'flows.builder.canvasControls.overview': 'Overview',
  'flows.builder.canvasControls.addHere': 'Add a node here',
  'flows.builder.canvasControls.removeLink': 'Remove link',
  'flows.builder.connect': 'Connect',
  'flows.builder.renameHint': 'Double-click to name this step',
  'flows.builder.stepNumber': 'Step {{n}}',
  'flows.builder.node.template': 'Send a template',
  'flows.builder.node.templateHint': 'An approved WhatsApp template, for messages outside the 24-hour window',
  'flows.builder.node.switch': 'Branch on a value',
  'flows.builder.node.switchHint': 'One path per value of a variable',
  'flows.builder.node.hours': 'Business hours',
  'flows.builder.node.hoursHint': 'Open or closed, by day and time',
  'flows.builder.node.split': 'Split test',
  'flows.builder.node.splitHint': 'Send contacts down branches at random, by weight',
  'flows.builder.llmMode.reply': 'Reply with AI',
  'flows.builder.llmMode.classify': 'Sort the message',
  'flows.builder.llmMode.extract': 'Pull out details',
  'flows.builder.llmModeHint.reply': 'Writes an answer, from your documents when some are ticked.',
  'flows.builder.llmModeHint.classify': 'Picks one of your labels for the message and leaves through that port.',
  'flows.builder.llmModeHint.extract': 'Reads details such as a name or an email address out of the message into variables.',
  'flows.builder.day.mon': 'Mon',
  'flows.builder.day.tue': 'Tue',
  'flows.builder.day.wed': 'Wed',
  'flows.builder.day.thu': 'Thu',
  'flows.builder.day.fri': 'Fri',
  'flows.builder.day.sat': 'Sat',
  'flows.builder.day.sun': 'Sun',
  'flows.builder.port.open': 'Open',
  'flows.builder.port.closed': 'Closed',
  'flows.builder.port.other': 'Other',
  'flows.builder.port.found': 'Found',
  'flows.builder.port.missing': 'Missing',
  'flows.builder.port.else': 'Else',
  'flows.builder.field.llmMode': 'What the AI does',
  'flows.builder.field.llmKnowsHint': 'The AI is told who it is talking to (name, email, tags, every answer collected so far) and remembers the last few exchanges, so "yes" after an offer works. It speaks as the assistant named under Settings.',
  'flows.builder.field.labels': 'Labels',
  'flows.builder.field.labelsHint': 'The AI picks the one that fits the message best. Each label is a port on the card; Other catches the rest.',
  'flows.builder.field.labelId': 'Label id',
  'flows.builder.field.labelTitle': 'Meaning',
  'flows.builder.field.labelTitlePlaceholder': 'Questions about invoices or payments',
  'flows.builder.field.addLabel': 'Add label',
  'flows.builder.field.extractFields': 'Details to pull out',
  'flows.builder.field.extractFieldsHint': 'Each row becomes a variable. The description tells the AI what to look for.',
  'flows.builder.field.fieldDescription': 'What to look for',
  'flows.builder.field.fieldDescriptionPlaceholder': 'The email address they gave',
  'flows.builder.field.saveAsClassifyHint': 'The chosen label is then {{llm.label}}.',
  'flows.builder.field.saveAsExtractHint': 'Each detail becomes its own variable, such as {{email}}. The whole set is {{llm.fields}}.',
  'flows.builder.field.kbUpload': 'Add a document',
  'flows.builder.field.kbUploadHint': 'PDF, text, Markdown, HTML or CSV. It is read, indexed and ticked for this step.',
  'flows.builder.field.kbManage': 'Every document lives in the',
  'flows.builder.field.template': 'Template',
  'flows.builder.field.templateChoose': 'Choose a template',
  'flows.builder.field.templateNone': 'No approved templates yet. Create and submit one under',
  'flows.builder.field.templateVariables': 'Template variables',
  'flows.builder.field.addVariable': 'Add variable',
  'flows.builder.field.switchVariable': 'Variable to check',
  'flows.builder.field.cases': 'Cases',
  'flows.builder.field.casesHint': 'One branch per row. Separate several values with commas, for example "yes, yeah, ok". Anything else leaves through the Else port.',
  'flows.builder.field.caseValue': 'Value',
  'flows.builder.field.addCase': 'Add case',
  'flows.builder.field.openDays': 'Open on',
  'flows.builder.field.opens': 'Opens',
  'flows.builder.field.closes': 'Closes',
  'flows.builder.field.timezone': 'Time zone',
  'flows.builder.field.timezoneHint': 'An IANA name such as Africa/Johannesburg.',
  'flows.builder.field.hoursPorts': 'Open leads out of the Open port, otherwise Closed. {{business_hours.local_time}} holds the local time.',
  'flows.builder.field.splitHint': 'Each contact is sent down one branch at random, in proportion to the weights. Use it to try two versions of a message.',
  'flows.builder.field.branchName': 'Branch name',
  'flows.builder.field.weight': 'Weight',
  'flows.builder.field.addBranch': 'Add branch',
  'flows.builder.field.optOut': 'Opt out',
  'flows.builder.field.optOutHint': 'Marks the contact as not wanting messages. Broadcasts skip them.',
  'flows.builder.summary.llmDefault': 'Answers with AI',
  'flows.builder.summary.noLabels': 'No labels yet',
  'flows.builder.summary.noFields2': 'No details to pull out yet',
  'flows.builder.summary.template': 'Sends {{name}}',
  'flows.builder.summary.noTemplate': 'No template chosen',
  'flows.builder.summary.switch': 'Branches on {{variable}}',
  'flows.builder.summary.noVariable': 'No variable chosen',
  'flows.validate.llmNoLabels': 'Add at least one label to sort into.',
  'flows.validate.llmNoFields': 'Add at least one detail to pull out.',
  'flows.validate.templateNone': 'Choose a template.',
  'flows.validate.switchNoVariable': 'Choose the variable to check.',
  'flows.validate.switchNoCases': 'Add at least one case.',
  'flows.validate.hoursBadTime': 'Opening and closing times must be HH:mm.',
  'flows.node.template': 'Template',
  'flows.node.switch': 'Branch',
  'flows.node.hours': 'Business hours',
  'flows.node.split': 'Split test',
  'flows.connect.title': 'Connect {{name}}',
  'flows.connect.whatsapp': 'WhatsApp',
  'flows.connect.live': 'Live',
  'flows.connect.notActive': 'Not active',
  'flows.connect.anyMessage': 'any message',
  'flows.connect.whatsappAny': 'Any message to {{number}} starts this flow.',
  'flows.connect.whatsappKeyword': 'A message containing "{{word}}" to {{number}} starts this flow.',
  'flows.connect.whatsappWebhook': 'This flow starts from the API only, not from an inbound message.',
  'flows.connect.activateHint': 'Switch the flow on from its page for it to answer real customers.',
  'flows.connect.numbers': 'Answers on these numbers',
  'flows.connect.noNumbers': 'No WhatsApp number attached yet. Attach one under',
  'flows.connect.manageNumbers': 'Manage numbers',
  'flows.connect.api': 'From your own system',
  'flows.connect.apiHint': 'Start this flow for a customer from your CRM, website or shop. Variables you pass are available to every step.',
  'flows.connect.apiKeys': 'API keys are created under',
  'flows.connect.otherChannels': 'Other channels',
  'flows.connect.messenger': 'Facebook Messenger',
  'flows.connect.instagram': 'Instagram',
  'flows.connect.website': 'Website chat',
  'flows.connect.metaChannelHint': 'is not available yet. It needs the Meta channel adapter and app review; the flow itself needs no change.',
  'flows.connect.websiteHint': 'is not available yet. It needs the web widget and a website channel adapter.',
  'flows.connect.engineReady': 'The flow engine does not care which channel a message came from: the same flow will run on these once the adapter for each is in place.',
  'flows.connect.leads': 'Capturing leads and using the name',
  'flows.connect.leads1': 'Ask for what you need with Ask a question steps (name, email, budget), or let Pull out details read them from a free-text reply.',
  'flows.connect.leads2': 'Add an Update contact step: set the name, save the answers as fields and add a tag such as "lead".',
  'flows.connect.leads3': 'From then on every message and AI reply can use {{contact.name}}, and the AI is told who it is talking to.',
  'flows.connect.leads4': 'Find the leads under Contacts by tag, or send them to your CRM with an HTTP request step.',
  'flows.connect.knowledgeHint': 'For answers from your own documents, upload them to the',
  'flows.test.templateTag': 'WhatsApp template',
  'flows.builder.assistant': 'Build with AI',
  'flows.actions.createWithAi': 'Create with AI',
  'flows.assistant.newFlowName': 'New flow',
  'flows.assistant.title': 'Build with AI',
  'flows.assistant.welcome': 'Tell me what the bot should do and I will build the steps. For example: "Greet people, ask for their name and email, then hand over to someone." You can also go one step at a time.',
  'flows.assistant.welcomeExisting': 'Tell me what to change in "{{name}}": add a step, remove one, reword a message, or rename a step. Steps are numbered as on the canvas.',
  'flows.assistant.placeholder': 'What should the bot do?',
  'flows.assistant.send': 'Send',
  'flows.assistant.thinking': 'Working on it…',
  'flows.assistant.applied': 'Put on the canvas',
  'flows.assistant.undo': 'Undo',
  'flows.assistant.clear': 'Start over',
  'flows.assistant.changes': 'Changes',
  'flows.assistant.problems': 'Check these before saving',
  'flows.assistant.suggestions': 'You could say',
  'flows.assistant.needs': 'Needed for this flow',
  'flows.assistant.need.document': 'A document to answer from',
  'flows.assistant.need.template': 'An approved template',
  'flows.assistant.need.number': 'A WhatsApp number',
  'flows.assistant.need.api': 'A system to send to',
  'flows.assistant.need.info': 'A detail',
  'flows.assistant.uploadHint': 'Upload it here and I will attach it to the AI step.',
  'flows.assistant.uploaded': 'I uploaded "{{name}}" (id {{id}}). Use it for the AI answer step.',
  'flows.assistant.orKnowledge': 'Or manage documents in the',
  'flows.assistant.apiHint': 'Paste the URL (and any key) in the chat and I will fill in the HTTP step.',
  'flows.assistant.error': 'The assistant did not answer. Try again.',
  'flows.assistant.stubHint': 'No AI provider is set up, so a rule-based helper answers. For the full assistant, add Bedrock under',
  'flows.assistant.starter1': 'Greet people, ask for their name and email, then hand over to someone',
  'flows.assistant.starter2': 'Answer questions from our price list',
  'flows.assistant.starter3': 'Take bookings: ask for the name, date and number of people, then confirm',
  'flows.builder.node.notify': 'Notify your team',
  'flows.builder.node.notifyHint': 'Email, SMS, WhatsApp or your own system, with what the customer needs',
  'flows.node.notify': 'Notify team',
  'flows.builder.port.answered': 'Answered',
  'flows.builder.port.unsure': 'Not sure',
  'flows.builder.summary.llmAll': 'Answers from the whole knowledge base',
  'flows.builder.summary.llmSelected': 'Answers from {{kb}} document(s)',
  'flows.builder.summary.llmNone': 'Answers from the AI alone, no documents',
  'flows.builder.summary.notifyNone': 'No one to notify yet',
  'flows.validate.notifyNone': 'Add at least one way to reach your team.',
  'flows.validate.notifyBadTarget': '"{{value}}" is not a valid address, number or web address.',
  'flows.builder.field.kbScope': 'Knowledge to answer from',
  'flows.builder.kbScope.all': 'Every document in the knowledge base',
  'flows.builder.kbScope.selected': 'Only the documents ticked below',
  'flows.builder.kbScope.none': 'None, the AI on its own',
  'flows.builder.kbScopeHint.all': 'New documents are used the moment they are uploaded.',
  'flows.builder.kbScopeHint.selected': 'Good for a step that should only know one thing, such as a price list.',
  'flows.builder.kbScopeHint.none': 'General knowledge only. It will not know your prices or policies.',
  'flows.builder.field.kbAllHint': 'Add or remove documents in the',
  'flows.builder.field.sendReply': 'Send the answer to the customer',
  'flows.builder.field.sendReplyHint': 'Off: the answer is only kept as {{llm.text}} for a later step to use.',
  'flows.builder.field.unsureBelow': 'Treat the answer as "not sure" below',
  'flows.builder.field.unsureBelowHint': 'When the AI is this unsure, nothing is sent and the flow leaves through the Not sure port. Link it to a step that asks what the customer needs and notifies your team.',
  'flows.builder.field.unsurePercent': '{{value}}% confidence',
  'flows.builder.field.notifyIntro': 'Sends your team what the customer asked for, the details collected so far and the conversation. The customer is not messaged by this step.',
  'flows.builder.field.notifyChannels': 'Reach the team by',
  'flows.builder.field.notifyVia': 'How',
  'flows.builder.field.notifyTarget': 'Address, number or web address',
  'flows.builder.field.addChannel': 'Add another way',
  'flows.builder.notifyChannel.email': 'Email',
  'flows.builder.notifyChannel.sms': 'SMS',
  'flows.builder.notifyChannel.whatsapp': 'WhatsApp (a staff number)',
  'flows.builder.notifyChannel.webhook': 'Your own system (web API)',
  'flows.builder.field.smsHint': 'An SMS to this number, charged per SMS part from your credits. If SMS is not available yet, it is skipped and the other ways still go out.',
  'flows.builder.field.whatsappStaffHint': 'WhatsApp only delivers a free-form message to a number that has messaged your business in the last 24 hours. For a staff number that never does, use email or SMS.',
  'flows.builder.field.webhookPayloadHint': 'Your system receives a JSON POST with subject, message, contact, details and transcript.',
  'flows.builder.field.notifySubject': 'Subject',
  'flows.builder.field.notifySubjectPlaceholder': 'New request from {{contact.name ?? contact.msisdn}}',
  'flows.builder.field.notifyMessage': 'Note to the team',
  'flows.builder.field.notifyMessageHint': 'Optional. The customer\'s details and the conversation are added below it automatically.',
  'flows.builder.field.includeDetails': 'Include what the customer told us',
  'flows.builder.field.includeDetailsHint': 'Every answer collected in this flow, one per line.',
  'flows.builder.field.includeTranscript': 'Include the conversation so far',
  'flows.builder.field.includeTranscriptHint': 'What the customer said and what the bot replied.',
  'flows.connect.websiteIntro': 'Paste the snippet before </body> on any page. A chat bubble appears bottom right, introduces itself with the bot\'s name, and the same flows answer there as on WhatsApp.',
  'flows.connect.siteName': 'Website name',
  'flows.connect.siteNamePlaceholder': 'Main website',
  'flows.connect.createSite': 'Create a site key',
  'flows.connect.addSite': 'Add another site',
  'flows.connect.siteCreated': 'Site key created. Paste the snippet on your site.',
  'flows.connect.websiteSnippetHint': 'Every flow that is switched on can answer on the site. To keep one to WhatsApp only, set its trigger\'s channels.',
  'flows.ws.sidebar': 'Flows',
  'flows.ws.collapse': 'Hide the flows',
  'flows.ws.expand': 'Show the flows',
  'flows.ws.entryPoints': 'Entry points',
  'flows.ws.entryPointsHint': 'How a conversation starts. A flow with keywords always wins; then these, in this order.',
  'flows.ws.yourFlows': 'Your flows',
  'flows.ws.search': 'Find a flow',
  'flows.ws.none': 'No keyword flows yet. Create one, or let the assistant build it.',
  'flows.ws.setUp': 'Set up',
  'flows.ws.setUpFromSidebar': 'Set it up from the list on the right.',
  'flows.ws.setUpOnKnowledge': 'Set up on the Knowledge base page',
  'flows.ws.notSetUp': 'Not set up',
  'flows.ws.openBuilder': 'Open in the builder',
  'flows.ws.configure': 'Configure',
  'flows.ws.allFlows': 'All flows',
  'flows.ws.title': '{{name}}, your bot',
  'flows.ws.description': 'What it does when someone writes, where it answers, and what it could not answer.',
  'flows.ws.persona': 'Name and tone',
  'flows.ws.channels': 'Where it answers',
  'flows.ws.channelsLine': '{{numbers}} WhatsApp number(s) and {{sites}} website(s). Messenger and Instagram arrive with their adapters.',
  'flows.ws.questions': 'Questions it could not answer',
  'flows.ws.questionsHint': 'Every question the AI was not sure about lands here. Add the answer and the bot knows it next time; no phrasing lists needed.',
  'flows.ws.questionsSummary': 'Last 7 days: {{asked}} asked, {{answered}} answered, {{unanswered}} unanswered',
  'flows.ws.noMisses': 'Nothing unanswered',
  'flows.ws.noMissesHint': 'When the bot is not sure about a question, it appears here with a box to type the answer.',
  'flows.ws.addAnswer': 'Add answer',
  'flows.ws.dismiss': 'Dismiss',
  'flows.ws.answerLabel': 'The answer the bot should give',
  'flows.ws.answerHint': 'Written as you would say it to the customer. It joins the knowledge base as a question and answer.',
  'flows.ws.addDocumentInstead': 'Add a document instead',
  'flows.ws.saveAnswer': 'Save answer',
  'flows.ws.answerAdded': 'Added to the knowledge base. The bot answers it from now on.',
  'flows.ws.questionClosed': 'Question closed.',
  'flows.ws.viewRun': 'view the conversation',
  'flows.entry.first_contact.name': 'Welcome new customers',
  'flows.entry.first_contact.hint': 'Runs the first time a person ever writes. Say who the bot is and how it can help.',
  'flows.entry.greeting.name': 'Returning customers',
  'flows.entry.greeting.hint': 'Runs when someone known says hello and nothing more.',
  'flows.entry.default.name': 'Anything else',
  'flows.entry.default.hint': 'Runs for any message no keyword flow claimed: answers from the knowledge base and hands over when unsure.',
  'flows.entry.post_resolution.name': 'After resolution',
  'flows.entry.post_resolution.hint': 'Runs when an agent resolves a conversation: a quick "was this helpful?".',
  'flows.entry.noAgent.name': 'No agent available',
  'flows.entry.noAgent.hint': 'Not a flow of its own: a port on the Hand off step. Link it to take a message or notify the team when nobody is online.',
  'flows.entry.template.welcomeName': 'Welcome',
  'flows.entry.template.welcome': 'Hi {{contact.name ?? there}}, I am {{bot.name}}. Ask me anything about our products and services; when I cannot help, a colleague will. By chatting here you agree that we keep your messages to assist you.',
  'flows.entry.template.consentName': 'Mark as welcomed',
  'flows.entry.template.askMore': 'I am not sure about that one yet. Tell me a little more and I will get the right person to help.',
  'flows.entry.template.returningName': 'Welcome back',
  'flows.entry.template.returning': 'Welcome back {{contact.name ?? there}}! It is {{bot.name}} again. How can I help today?',
  'flows.entry.template.rateName': 'Ask for a rating',
  'flows.entry.template.rate': 'Thanks for chatting with us. Did we sort out what you needed?',
  'flows.entry.template.rateYes': 'Yes, thanks',
  'flows.entry.template.rateNo': 'Not really',
  'flows.entry.template.tagYesName': 'Note a happy customer',
  'flows.entry.template.tagNoName': 'Note an unhappy customer',
  'flows.entry.template.thanks': 'Great to hear. Have a good day!',
  'flows.entry.template.sorry': 'Sorry to hear that. A colleague will look at this again and come back to you.',
  'flows.entry.template.handoffName': 'Hand over',
  'flows.entry.template.handoff': 'One moment, a colleague will be with you.',
  'flows.entry.template.noAgent': 'Nobody is available right now. We have your message and will reply as soon as someone is back.',
  'flows.builder.trigger.firstContact': 'First contact (a new person)',
  'flows.builder.trigger.greeting': 'A greeting from someone known',
  'flows.builder.trigger.default': 'Anything else (default)',
  'flows.builder.trigger.postResolution': 'After an agent resolves the conversation',
  'flows.builder.port.noAgent': 'No agent',
  'flows.builder.node.flow': 'Run another flow',
  'flows.builder.node.flowHint': 'Continue in another flow, keeping what was collected',
  'flows.node.flow': 'Run flow',
  'flows.builder.field.flowToRun': 'Flow to run',
  'flows.builder.field.flowToRunHint': 'The run continues in that flow from its first step. Everything collected so far stays available.',
  'flows.builder.field.flowChoose': 'Choose a flow',
  'flows.builder.field.flowToRunNote': 'A flow that is switched off is skipped and the run continues here. A flow cannot run itself.',
  'flows.builder.summary.flowNone': 'No flow chosen',
  'flows.validate.flowNone': 'Choose the flow to run.',
  'flows.builder.addStep': 'Add a step',
  'flows.builder.flowsButton': 'Flows',
  'flows.builder.flowsDrawer.title': 'Your flows',
  'flows.builder.flowsDrawer.hint': 'Click a flow to open it here. The gear changes its name, trigger and whether it is on.',
  'flows.builder.flowsDrawer.noDescription': 'No description yet',
  'flows.builder.flowsDrawer.current': 'Open now',
  'flows.builder.flowsDrawer.switchTitle': 'Leave without saving?',
  'flows.builder.flowsDrawer.switchBody': 'This flow has unsaved changes. Save first, or discard them and open the other flow.',
  'flows.builder.flowsDrawer.switchConfirm': 'Discard and open',
  'flows.builder.problemsToggle': '{{count}} to fix',
  'flows.builder.problemsHide': 'Hide',
  'flows.builder.jsonDrawer': 'Definition (JSON)',
  'flows.builder.more': 'More',
  'flows.builder.descriptionEmpty': 'No description. Add one with the gear next to the name.',
  'flows.builder.picker.title': 'Add a step',
  'flows.builder.picker.search': 'Search steps',
  'flows.builder.picker.empty': 'No step matches. Try another word, or ask the assistant.',
  'flows.builder.picker.useWhen': 'Use it when',
  'flows.builder.picker.outputs': 'It leaves through',
  'flows.builder.picker.oneOutput': 'One path: the next step you link.',
  'flows.builder.picker.noOutputsEnds': 'Nothing: the flow ends here (or continues in the other flow).',
  'flows.builder.picker.assistantLink': 'Not sure which? Ask the assistant to build it',
  'flows.builder.picker.add': 'Add {{name}}',
  'flows.builder.picker.groups.trigger': 'Start',
  'flows.builder.picker.groups.messages': 'Messages',
  'flows.builder.picker.groups.ask': 'Ask the customer',
  'flows.builder.picker.groups.ai': 'AI',
  'flows.builder.picker.groups.people': 'People and contacts',
  'flows.builder.picker.groups.logic': 'Logic',
  'flows.builder.picker.groups.connect': 'Your systems',
  'flows.builder.picker.long.trigger': 'How this flow starts: a keyword in a message, the first contact from a new person, a greeting, anything no other flow claimed, after an agent resolves a conversation, or a call from your own system.',
  'flows.builder.picker.when.trigger': 'Every flow needs one. A keyword flow always wins over the entry points.',
  'flows.builder.picker.long.send': 'Sends a message: text, an image, a document, up to three buttons or a list. Use {{variables}} to insert the customer\'s name or an answer they gave earlier.',
  'flows.builder.picker.when.send': 'A greeting, a confirmation, a menu of choices that leads to other steps.',
  'flows.builder.picker.long.template': 'Sends one of your approved WhatsApp templates, with its parameters filled from variables. WhatsApp only delivers free-form text within 24 hours of the customer\'s last message; a template goes through at any time.',
  'flows.builder.picker.when.template': 'The first message of a conversation your business starts, or a follow-up after the 24-hour window.',
  'flows.builder.picker.long.ask': 'Asks a question and waits for the reply, which is stored as a variable for every later step. It can insist on an email address, a number or a date and ask again politely, or offer choices as buttons, each with its own path.',
  'flows.builder.picker.when.ask': 'Collecting a name, an email, an order number, or letting the customer choose between options.',
  'flows.builder.picker.long.llm': 'Answers with AI, speaking as your named assistant. In Reply mode it answers from your knowledge base and says when it is not sure; in Sort mode it decides what the customer wants and leaves through the matching port; in Pull-out mode it reads details such as an email out of a message into variables.',
  'flows.builder.picker.when.llm': 'Questions about your products and policies, routing by intent, or reading details from free text.',
  'flows.builder.picker.long.http': 'Calls an external API with the details collected so far and keeps what comes back as variables. Your CRM, an order system, a booking calendar.',
  'flows.builder.picker.when.http': 'Looking up an order, creating a lead in your CRM, checking availability.',
  'flows.builder.picker.long.contact': 'Writes to the contact book: the customer\'s name, email, custom fields and tags. Tags are the groups broadcasts can target, and how leads are found later.',
  'flows.builder.picker.when.contact': 'Saving what was collected so it is there next time, or tagging a lead.',
  'flows.builder.picker.long.notify': 'Tells your team by email, SMS, a WhatsApp message to a staff number, or a call to your own system: what the customer asked for, the details collected and the conversation so far. The customer is not messaged by this step.',
  'flows.builder.picker.when.notify': 'A request the bot cannot finish, a new lead, anything a person should see now.',
  'flows.builder.picker.long.handoff': 'Hands the conversation to a person in the inbox and stops the bot for that customer, leaving the agent a summary note. If nobody is available it can take another path.',
  'flows.builder.picker.when.handoff': 'When a person is needed and someone is there to take it.',
  'flows.builder.picker.long.flow': 'Continues in another flow from its first step, keeping everything collected so far. Build a booking or lead flow once and reuse it from any menu.',
  'flows.builder.picker.when.flow': 'Reusing a flow from several places.',
  'flows.builder.picker.long.condition': 'Checks rules against what the flow knows (an answer, a contact field, an API result) and takes the Yes or No path.',
  'flows.builder.picker.when.condition': 'Big party versus small, existing customer versus new, order found versus not.',
  'flows.builder.picker.long.switch': 'One path per value of a variable, with an Else path for anything unexpected.',
  'flows.builder.picker.when.switch': 'Routing on a choice or a label with several possible values.',
  'flows.builder.picker.long.hours': 'Open or Closed, by day and time in your time zone, with the local time available as a variable.',
  'flows.builder.picker.when.hours': 'An after-hours reply, or a hand-over only while someone is in.',
  'flows.builder.picker.long.split': 'Sends each customer down one branch at random, in proportion to the weights you set.',
  'flows.builder.picker.when.split': 'Trying two wordings of a message and comparing the results.',
  'flows.builder.picker.long.set': 'Stores values in variables for later steps: a computed total, a default, a copy of an answer.',
  'flows.builder.picker.when.set': 'Keeping something for later without asking the customer.',
  'flows.builder.picker.long.delay': 'Waits, from seconds to seven days, then continues. A new message from the customer during the wait starts a fresh run instead.',
  'flows.builder.picker.when.delay': 'A short pause so a message can be read, or a follow-up the next day.',
  'flows.builder.picker.long.end': 'Ends the flow, with an optional last message. Every path should end on purpose.',
  'flows.builder.picker.when.end': 'The last step of any path.',
  'flows.builder.closeInspector': 'Close properties',
  'flows.builder.canvasControls.insertHere': 'Insert a step here',
  'flows.builder.flowSettings': 'Flow settings',
  'flows.builder.jsonShort': 'JSON',
  'flows.builder.node.form': 'Form',
  'flows.builder.node.formHint': 'Several questions, one set of answers to send anywhere',
  'flows.builder.picker.long.form': 'Asks a list of fields one at a time (a name, an email address, a phone number, a choice, a date…), checks each answer by its type and tidies it, then composes them into one set of answers. Send it to your system with an HTTP request step, save it to the contact, or read it back to the customer to confirm.',
  'flows.builder.picker.when.form': 'A booking, a lead, a quote request, a support ticket: any time you collect three or more details for a system.',
  'flows.builder.port.submitted': 'Submitted',
  'flows.builder.port.cancelled': 'Cancelled',
  'flows.builder.summary.formEmpty': 'No fields yet',
  'flows.builder.summary.formFields': '{{count}} fields ({{names}}) → {{var}}',
  'flows.validate.formNoFields': 'The form has no fields yet.',
  'flows.validate.formBadField': 'Field "{{name}}" needs a name of letters, digits and underscores.',
  'flows.validate.formDuplicateField': 'Two fields are saved as "{{name}}".',
  'flows.validate.formNoQuestion': 'Field "{{name}}" has no question.',
  'flows.validate.formChoiceNoOptions': 'Field "{{name}}" is a choice but has no options.',
  'flows.validate.httpAuthMissing': 'The {{type}} authentication is missing its {{field}}.',
  'flows.validate.httpReplyEmpty': 'The reply to the customer has nothing to show: add a field, a line or a fallback, or switch it off.',
  'flows.builder.validate.text': 'Some text, with a length limit',
  'flows.builder.validate.name': 'A person’s name',
  'flows.builder.validate.integer': 'A whole number',
  'flows.builder.validate.date': 'A date',
  'flows.builder.validate.time': 'A time',
  'flows.builder.validate.yes_no': 'Yes or no',
  'flows.builder.validate.id_number': 'A South African ID number',
  'flows.builder.validate.url': 'A web address',
  'flows.builder.validate.postcode': 'A postal code',
  'flows.builder.validate.choice': 'One of the choices',
  'flows.builder.validateDesc.none': 'Whatever they send is stored as typed.',
  'flows.builder.validateDesc.not_empty': 'Anything but a blank message.',
  'flows.builder.validateDesc.text': 'Any text between the minimum and maximum length.',
  'flows.builder.validateDesc.name': 'One to four words, letters only. A sentence, an email address or a phone number is asked again; “my name is thandi nkosi” is stored as Thandi Nkosi.',
  'flows.builder.validateDesc.email': 'A real-looking address, stored in lower case.',
  'flows.builder.validateDesc.phone': 'Digits with an optional dialling code. Stored in international form, e.g. +27821234567; a local number gets the country’s code.',
  'flows.builder.validateDesc.number': 'Decimals allowed. Stored as a number, so it can be compared.',
  'flows.builder.validateDesc.integer': 'A whole number, stored as a number.',
  'flows.builder.validateDesc.date': 'Day first: 12/03/2026, 12 March, tomorrow, Friday. Stored as 2026-03-12.',
  'flows.builder.validateDesc.time': '14:30, 2pm, 2:30 pm, 14h30, noon. Stored as 14:30.',
  'flows.builder.validateDesc.yes_no': 'Yes, ja, yebo, sure, ok… or no, nee, nope… Stored as yes or no.',
  'flows.builder.validateDesc.id_number': '13 digits with a valid date of birth and check digit.',
  'flows.builder.validateDesc.url': 'A web address; https:// is added when it was left out.',
  'flows.builder.validateDesc.postcode': 'Four digits for South Africa; letters and digits elsewhere.',
  'flows.builder.validateDesc.regex': 'Matches the pattern you write.',
  'flows.builder.validateDesc.choice': 'One of the choices below, sent as buttons (up to three) or a list.',
  'flows.builder.retryDefault.name': 'Please send just your name, for example: Thandi Nkosi.',
  'flows.builder.retryDefault.email': 'That does not look like an email address. Please send it like name@example.com.',
  'flows.builder.retryDefault.phone': 'Please send a phone number we can reach you on, for example 082 123 4567 or +27 82 123 4567.',
  'flows.builder.retryDefault.number': 'A number, please.',
  'flows.builder.retryDefault.integer': 'A whole number, please.',
  'flows.builder.retryDefault.date': 'Please send a date, for example 12/03/2026 or 12 March.',
  'flows.builder.retryDefault.time': 'Please send a time, for example 14:30 or 2pm.',
  'flows.builder.retryDefault.yes_no': 'Please answer yes or no.',
  'flows.builder.retryDefault.id_number': 'Please send a valid 13-digit South African ID number.',
  'flows.builder.retryDefault.url': 'Please send a web address, for example https://example.com.',
  'flows.builder.retryDefault.postcode': 'Please send a postal code, for example 2196.',
  'flows.builder.retryDefault.text': 'Please send a little more detail.',
  'flows.builder.retryDefault.choice': 'Please choose one of the options.',
  'flows.builder.field.retryDefaultHint': 'Leave empty to use the built-in message: “{{text}}”',
  'flows.builder.field.country': 'Country',
  'flows.builder.field.countryHint': 'Used when the customer leaves out the dialling code.',
  'flows.builder.field.minLength': 'Minimum length',
  'flows.builder.field.maxLength': 'Maximum length',
  'flows.builder.country.ZA': 'South Africa',
  'flows.builder.country.NA': 'Namibia',
  'flows.builder.country.BW': 'Botswana',
  'flows.builder.country.ZW': 'Zimbabwe',
  'flows.builder.country.MZ': 'Mozambique',
  'flows.builder.country.LS': 'Lesotho',
  'flows.builder.country.SZ': 'Eswatini',
  'flows.builder.country.ZM': 'Zambia',
  'flows.builder.country.MW': 'Malawi',
  'flows.builder.country.KE': 'Kenya',
  'flows.builder.country.NG': 'Nigeria',
  'flows.builder.country.GH': 'Ghana',
  'flows.builder.country.TZ': 'Tanzania',
  'flows.builder.country.UG': 'Uganda',
  'flows.builder.country.GB': 'United Kingdom',
  'flows.builder.country.US': 'United States',
  'flows.builder.country.AU': 'Australia',
  'flows.builder.country.DE': 'Germany',
  'flows.builder.country.IN': 'India',
  'flows.builder.country.AE': 'United Arab Emirates',
  'flows.builder.field.formIntro': 'Opening line',
  'flows.builder.field.formIntroHint': 'Sent once before the first question. Optional.',
  'flows.builder.field.formFields': 'Fields',
  'flows.builder.field.formFieldsHint': 'Asked in this order, one at a time. Each answer is checked by its type before the next question.',
  'flows.builder.field.fieldQuestion': 'Question',
  'flows.builder.field.fieldSavedAs': 'Saved as',
  'flows.builder.field.fieldType': 'Answer must be',
  'flows.builder.field.fieldRequired': 'Required',
  'flows.builder.field.fieldRequiredHint': 'Off: the customer can type skip.',
  'flows.builder.field.fieldOptions': 'Choices',
  'flows.builder.field.fieldOptionsHint': 'One per line. Up to three show as buttons, more as a list.',
  'flows.builder.field.formNoFields': 'No fields yet. Add the first one.',
  'flows.builder.field.formConfirm': 'Read the answers back',
  'flows.builder.field.formConfirmHint': 'After the last field the bot lists the answers and asks “Is this correct?” with Yes and No buttons. No starts the form again.',
  'flows.builder.field.formDone': 'Message when done',
  'flows.builder.field.formDoneHint': 'Optional. Sent once every answer is in.',
  'flows.builder.field.formCancel': 'Message when cancelled',
  'flows.builder.field.formCancelHint': 'The customer can type cancel or stop at any point. Optional.',
  'flows.builder.field.saveAsFormHint': 'The answers become one object with this name (form unless you change it): form.email is one answer, and an HTTP request step can send the whole thing as its body.',
  'flows.builder.field.formPreview': 'What the answers look like',
  'flows.builder.field.formPorts': 'Ports: Submitted once every required answer is in; Cancelled when the customer says cancel or a required answer never comes.',
  'flows.builder.field.moveUp': 'Move up',
  'flows.builder.field.moveDown': 'Move down',
  'flows.builder.field.auth': 'Authentication',
  'flows.builder.auth.none': 'None',
  'flows.builder.auth.bearer': 'Bearer token',
  'flows.builder.auth.basic': 'Username and password (Basic)',
  'flows.builder.auth.api_key': 'API key',
  'flows.builder.auth.custom_header': 'Custom header',
  'flows.builder.auth.oauth2': 'OAuth2 client credentials',
  'flows.builder.authHint.none': 'The request is sent as it is. Add headers below if the API wants them.',
  'flows.builder.authHint.bearer': 'Sent as Authorization: Bearer <token>.',
  'flows.builder.authHint.basic': 'Sent as Authorization: Basic, encoded from the username and password.',
  'flows.builder.authHint.api_key': 'A key the API gave you, sent as a header or in the query string.',
  'flows.builder.authHint.custom_header': 'Any header the API expects, with its value.',
  'flows.builder.authHint.oauth2': 'The bot fetches a token from the token URL with the client ID and secret, keeps it until it expires, and sends it as a Bearer token.',
  'flows.builder.field.authToken': 'Token',
  'flows.builder.field.authUsername': 'Username',
  'flows.builder.field.authPassword': 'Password',
  'flows.builder.field.authKeyName': 'Key name',
  'flows.builder.field.authKeyValue': 'Key',
  'flows.builder.field.authKeyIn': 'Send the key',
  'flows.builder.field.authKeyInHeader': 'as a header',
  'flows.builder.field.authKeyInQuery': 'in the query string',
  'flows.builder.field.authHeaderName': 'Header name',
  'flows.builder.field.authHeaderValue': 'Header value',
  'flows.builder.field.authTokenUrl': 'Token URL',
  'flows.builder.field.authClientId': 'Client ID',
  'flows.builder.field.authClientSecret': 'Client secret',
  'flows.builder.field.authScope': 'Scope',
  'flows.builder.field.authClientAuth': 'Send the client credentials',
  'flows.builder.field.authClientAuthBody': 'in the form body',
  'flows.builder.field.authClientAuthBasic': 'as Basic auth',
  'flows.builder.field.authSecretHint': 'Stored encrypted and never shown again. Leave it empty to keep the saved one, or type a new one to replace it. Variables are allowed. A copy of the flow does not take it with it.',
  'flows.builder.field.bodySource': 'Body',
  'flows.builder.bodySource.json': 'JSON I write',
  'flows.builder.bodySource.form': 'The answers of a form step',
  'flows.builder.bodySource.text': 'Plain text',
  'flows.builder.field.bodyForm': 'Form step',
  'flows.builder.field.bodyFormHint': 'Its answers are sent as the JSON body, exactly as collected.',
  'flows.builder.field.bodyFormNone': 'No form step in this flow yet. Add one before this step.',
  'flows.builder.field.bodyFormChoose': 'Choose the form…',
  'flows.builder.field.probe': 'Test this request',
  'flows.builder.field.probeHint': 'Makes the real call now, with the sample values below, and lists every field the response offers so you can pick what to keep and what to show.',
  'flows.builder.field.probeRun': 'Send test request',
  'flows.builder.field.probeSample': 'Sample values',
  'flows.builder.field.probeSampleHint': 'The variables this request uses. Type a value, or JSON for a whole object such as a form’s answers.',
  'flows.builder.field.probeStatus': 'Answered with status {{status}}',
  'flows.builder.field.probeFailed': 'The request failed: {{error}}',
  'flows.builder.field.probePaths': 'Fields in the response',
  'flows.builder.field.probeExtract': 'Keep',
  'flows.builder.field.probeShow': 'Show',
  'flows.builder.field.probeBody': 'Response body',
  'flows.builder.field.probeReply': 'What the customer would see',
  'flows.builder.field.probeReplyNone': 'Nothing would be sent to the customer by this step.',
  'flows.builder.field.probeExtracted': 'Variables this response gives',
  'flows.builder.field.replySection': 'Reply to the customer from the response',
  'flows.builder.field.replySend': 'Send a reply from the response',
  'flows.builder.field.replySendHint': 'Off: this step sends nothing; a later Send message step can use the kept variables.',
  'flows.builder.field.replyText': 'A line of your own',
  'flows.builder.field.replyTextHint': 'Optional. Shown first. Variables are allowed.',
  'flows.builder.field.replyPaths': 'Fields to show',
  'flows.builder.field.replyPathsHint': 'Each on its own line. A label is put in front of the value; leave it blank to show the value alone.',
  'flows.builder.field.replyLabel': 'Label',
  'flows.builder.field.addReplyPath': 'Add a field',
  'flows.builder.field.replyFallback': 'If none of them is in the response',
  'flows.builder.field.replyFallbackHint': 'Optional. Sent instead, for example “Sorry, I could not fetch that.”',
  'flows.builder.pickOrType.pick': 'Choose…',
  'flows.builder.pickOrType.own': 'Type my own…',
  'flows.builder.pickOrType.back': 'Choose from the list instead',
  'flows.builder.field.variablePick': 'Variable',
  'flows.builder.links': 'Links',
  'flows.builder.linksHint': 'Where this step comes from and where it goes. Remove a link here, or hover a line on the canvas and use its ×.',
  'flows.builder.linkFrom': 'From {{step}}',
  'flows.builder.linkTo': 'To {{step}}',
  'flows.builder.linkVia': 'via {{port}}',
  'flows.builder.removeLink': 'Remove link',
  'flows.builder.noLinks': 'Not linked to any step yet.',
  'flows.builder.detach': 'Detach from its links',
  'flows.builder.detachHint': 'Keeps the step, removes every line to and from it.',
  'flows.columns.spend': 'Spend',
  'flows.spend.hint': '{{count}} charges: model tokens, replies and notifications, from the ledger',
  'flows.connect.metaChannelLive': 'answers here too, once a page is connected.',
  'flows.connect.metaChannelWhere': 'Connect a Facebook page or an Instagram account on',
  'bots.switcher.label': 'Chatbot',
  'bots.switcher.hint': 'You are looking at this chatbot\'s work.',
  'bots.switcher.flowsHint': 'The flows below belong to this chatbot. New flows are added to it.',
  'bots.switcher.knowledgeHint': 'Each chatbot has its own automatic answers. The documents are shared by all of them.',
  'bots.switcher.defaultOption': '{{name}} (default)',
  'bots.channels.title': 'Where this chatbot answers',
  'bots.channels.intro': 'Tick the numbers, website chats and accounts this chatbot should answer on. Each one is answered by one chatbot, so ticking it here takes it from the chatbot that had it.',
  'bots.channels.introDefault': 'This is your default chatbot: it answers wherever no other chatbot was chosen. To give a number or chat to another chatbot, open that chatbot and tick it there.',
  'bots.channels.noFlows': '{{name}} has no flows yet, so customers writing here get no answer. Build one under',
  'bots.channels.none': 'You have no numbers, website chats or accounts connected yet. Start with',
  'bots.channels.kind.whatsapp': 'WhatsApp',
  'bots.channels.kind.web': 'Website chat',
  'bots.channels.kind.messenger': 'Messenger',
  'bots.channels.kind.instagram': 'Instagram',
  'bots.channels.mineByDefault': 'Answers here because no other chatbot was chosen.',
  'bots.channels.answeredBy': 'Answered by {{name}} now.',
  'bots.channels.moved': '{{account}} is now answered by {{name}}.',
  'bots.channels.returned': '{{account}} went back to {{name}}.',
  'bots.channels.confirm.title': 'Move it to this chatbot?',
  'bots.channels.confirm.body': '{{account}} is answered by {{from}} today. From the next message it will be answered by {{to}}. A conversation that is already under way finishes with {{from}}.',
  'bots.channels.confirm.action': 'Move it',
  'bots.actions.title': 'Default and delete',
  'bots.actions.isDefault': 'This is your default chatbot. It answers on every number, website chat and account that was not given to another chatbot, so it cannot be deleted. To delete it, make another chatbot the default first.',
  'bots.actions.defaultHint': 'The default chatbot answers on every number, website chat and account that was not given to another chatbot.',
  'bots.actions.madeDefault': '{{name}} is now your default chatbot.',
  'bots.actions.deleted': '{{name}} was deleted.',
  'bots.actions.makeDefault.title': 'Make {{name}} the default?',
  'bots.actions.makeDefault.body': '{{name}} will answer wherever no chatbot was chosen, starting with the next message. Flows stay with the chatbot they belong to.',
  'bots.actions.makeDefault.action': 'Make default',
  'bots.actions.delete.title': 'Delete {{name}}?',
  'bots.actions.delete.body': 'This chatbot has no flows and answers nowhere, so nothing else changes. Past conversations keep their history.',
  'bots.actions.delete.action': 'Delete chatbot',
  'bots.actions.delete.hint': 'Deleting removes this chatbot\'s name and settings. Past conversations keep their history.',
  'bots.actions.delete.hintInUse': 'This chatbot has flows or answers on an account. You will be asked which chatbot takes them over.',
  'bots.actions.move.body': '{{name}} still has work: flows {{flows}}, accounts {{accounts}}. Choose the chatbot that takes them over. They move in the same step as the delete.',
  'bots.actions.move.label': 'Move everything to',
  'bots.actions.move.hint': 'That chatbot runs these flows and answers on these accounts from the next message.',
  'bots.actions.move.action': 'Move and delete',
  'bots.add.title': 'Add a chatbot',
  'bots.add.intro': 'A chatbot has its own name, its own way of speaking and its own flows. After adding it, choose where it answers and build its flows.',
  'bots.add.copyFrom': 'Start from',
  'bots.add.copyFromHint': 'Copies the tone, language, small talk, word lists and notices. Flows and accounts are not copied.',
  'bots.add.copyNothing': 'A blank chatbot',
  'bots.add.action': 'Add a chatbot',
  'bots.add.done': '{{name}} was added. Choose where it answers, then build its flows.',
  'bots.add.cardTitle': 'Another chatbot',
  'bots.add.cardBody': 'Run a second brand, language or department with its own name, flows and numbers. One chatbot can answer on several numbers, or each number can have its own.',
  'bots.add.atLimit': 'Chatbots included in {{plan}}: {{count}}. Move up a plan to add another.',
  'bots.add.seePlans': 'See plans',
  'bots.list.title': 'Your chatbots',
  'bots.list.intro': 'Each chatbot has its own name, flows and the numbers and chats it answers on. Open one to change it.',
  'bots.list.default': 'Default',
  'bots.list.answers': 'Linked numbers and chats: {{count}}',
  'bots.list.answersDefault': 'Answers wherever no other chatbot was chosen',
  'bots.list.flows': 'Flows: {{count}}',
  'bots.loadError': 'Your chatbots could not be loaded. Refresh the page to try again.',
  'bots.backToList': 'Back to your chatbots',
  'bots.notFound': 'That chatbot no longer exists.',
  'bots.flow.label': 'Chatbot',
  'bots.flow.hint': 'Only this chatbot runs the flow. Moving it takes effect from the next message.',
  'bots.header.count': '{{current}} of {{allowance}} on {{plan}}',
  'bots.header.countUnlimited': '{{current}} on {{plan}}, no limit',
  'bots.header.single': '{{name}} answers on all your numbers and chats. Add a chatbot to run a second brand, language or department with its own name, flows and numbers.',
  'flows.connect.editSiteTitle': 'Edit website chat',
  'flows.connect.siteNameHint': 'Visitors see it under the chatbot\'s name at the top of the chat.',
  'flows.connect.greeting': 'Greeting',
  'flows.connect.greetingHint': 'The first message a visitor sees, up to 500 characters. Leave it empty to use the chatbot\'s own opening line.',
  'flows.connect.allowedSites': 'Allowed websites',
  'flows.connect.allowedSitesHint': 'One address per line, such as https://www.example.co.za. The chat opens only on these; leave it empty to allow any website.',
  'flows.connect.siteSaved': 'Website chat saved. Websites show the change the next time a page loads.',
  'flows.connect.onSites': 'On {{sites}}',
  'flows.connect.onAnySite': 'On any website',
  'flows.connect.removeSiteTitle': 'Remove {{name}}?',
  'flows.connect.removeSiteBody': 'The chat stops on every website that carries this snippet, and a new chat needs a new snippet. This cannot be undone.',
  'flows.builder.node.decision': 'Decision',
  'flows.builder.node.decisionHint': 'Several paths, chosen by rules',
  'flows.node.decision': 'Decision',
  'flows.builder.picker.long.decision': 'Checks rules in order and takes the first path that fits: a date, whether a customer detail is filled in, whether you know the customer, an earlier answer, or what your own system replied. Anything else goes to Otherwise.',
  'flows.builder.picker.when.decision': 'Members one way and new customers another, different replies before and after a date, or a path for each result your system can give.',
  'flows.builder.port.otherwise': 'Otherwise',
  'flows.builder.summary.decision': '{{n}} paths, first match wins',
  'flows.builder.summary.noPaths': 'No paths yet',
  'flows.builder.field.paths': 'Paths',
  'flows.builder.field.decisionOrderHint': 'Checked from the top. The first path whose rules are true is taken; if none is, the step leaves by Otherwise.',
  'flows.builder.field.pathLabel': 'Path {{n}}',
  'flows.builder.field.pathLabelPlaceholder': 'For example: Member',
  'flows.builder.field.pathMatch': 'Take this path when',
  'flows.builder.field.removePath': 'Remove this path',
  'flows.builder.field.addPath': 'Add a path',
  'flows.builder.field.decisionExistsHint': 'To check your own system (does this customer exist, is the order paid), put a Web API step before this one and add a rule on its result, for example its "ok" value is yes or true.',
  'flows.builder.field.decisionTimezoneHint': 'For rules on dates and times. Leave empty for South African time.',
  'flows.builder.field.dateRight': 'today, or a date such as 2026-12-25',
  'flows.builder.field.daysRight': 'Days',
  'flows.builder.varGroup.customer': 'Customer',
  'flows.builder.varGroup.answers': 'This chat',
  'flows.builder.varGroup.api': 'Web API',
  'flows.builder.varGroup.clock': 'Date and time',
  'flows.builder.varGroup.ai': 'AI',
  'flows.builder.varGroup.other': 'Other',
  'flows.builder.op.filled': 'is filled in',
  'flows.builder.op.not_filled': 'is not filled in',
  'flows.builder.op.is_true': 'is yes or true',
  'flows.builder.op.is_false': 'is no or false',
  'flows.builder.op.not_in': 'is not one of (comma-separated)',
  'flows.builder.op.is_today': 'is today',
  'flows.builder.op.on_date': 'is on',
  'flows.builder.op.before': 'is before',
  'flows.builder.op.after': 'is after',
  'flows.builder.op.on_or_before': 'is on or before',
  'flows.builder.op.on_or_after': 'is on or after',
  'flows.builder.op.within_days': 'is within the next (days)',
  'flows.builder.op.within_last_days': 'was within the last (days)',
  'flows.builder.op.older_than_days': 'is more than (days) ago',
  'flows.validate.decisionNoPaths': '{{id}} has no paths. Add at least one.',
  'flows.validate.decisionPathNoRules': '{{id}}: path "{{path}}" has no rule yet, so it is never taken.',
  'flows.validate.decisionRuleNoValue': '{{id}}: a rule on path "{{path}}" has nothing to compare with.',
  'flows.validate.decisionBadOp': '{{id}}: path "{{path}}" uses a check this step does not know ({{op}}).',
  'flows.validate.decisionBadNumber': '{{id}}: a rule on path "{{path}}" needs a whole number of days.',
  'flows.validate.decisionPathNoLabel': '{{id}}: give path {{path}} a name, so the link from it says where it goes.',
  'flows.validate.decisionPathUnlinked': '{{id}}: path "{{path}}" leads nowhere, so the chat ends for anyone it chooses.',
  'flows.builder.llmMode.rewrite': 'Put data into words',
  'flows.builder.llmModeHint.rewrite': 'Turns data (what your system replied, a form\'s answers, a list) into a short message a person can read. One way out.',
  'flows.builder.field.rewriteInput': 'The data',
  'flows.builder.field.rewriteInputHint': 'Pick what to put into words, for example what a Web API step returned.',
  'flows.builder.field.rewriteInstruction': 'What to tell the customer',
  'flows.builder.field.rewriteInstructionHint': 'For example: List the branches nearest first, with their trading hours. Leave empty to simply say what the data shows.',
  'flows.builder.field.rewriteSendHint': 'Send the wording straight to the customer. Switch it off to use it in a later step instead.',
  'flows.builder.field.saveAsRewriteHint': 'The wording is then {{llm.text}}.',
  'flows.builder.field.httpReplyAi': 'Let the AI put it into words',
  'flows.builder.field.httpReplyAiHint': 'Instead of sending fields as they are, the AI turns the response into a short, readable message. Charged as AI use.',
  'flows.builder.field.httpReplyAiPath': 'Which part of the response',
  'flows.builder.field.httpReplyAiPathHint': 'Leave empty for the whole response. Test the request below to pick from its parts.',
  'flows.builder.field.httpReplyAiLeadHint': 'Optional. Sent as it is, above what the AI writes.',
  'flows.builder.field.httpReplyAiFallbackHint': 'Sent if the AI cannot word the response.',
  'flows.builder.field.httpReplyAiLimit': 'The AI reads about the first 6 000 characters and says when there is more. Ask your system for fewer results where you can.',
  'flows.builder.field.httpReplyAiProbe': 'The AI will word this when the flow runs. Try it in Test flow.',
  'flows.builder.summary.llmRewrite': 'Puts {{input}} into words',
  'flows.builder.summary.llmRewriteNone': 'No data chosen',
  'flows.validate.llmRewriteNoInput': '{{id}} has no data to put into words.',
  'flows.validate.httpAiNoInstruction': '{{id}}: say what the AI should tell the customer, or it will simply describe the response.',
  'flows.builder.intent.title': 'When a customer asks for it',
  'flows.builder.intent.intro': 'Say what this flow is for, and a customer who asks for it in their own words is taken into it. No keyword is needed.',
  'flows.builder.intent.description': 'What this flow is for',
  'flows.builder.intent.descriptionHint': 'One sentence, the way a customer would say it. For example: I want to register for an event.',
  'flows.builder.intent.examples': 'Other ways customers say it',
  'flows.builder.intent.examplesHint': 'One per line. Three to six real sentences work best.',
  'flows.builder.intent.mode': 'When a message matches',
  'flows.builder.intent.modeJump': 'Start this flow straight away',
  'flows.builder.intent.modeOffer': 'Offer it as a button',
  'flows.builder.intent.modeOff': 'Do nothing',
  'flows.builder.intent.modeHint.jump': 'A clear request starts the flow. A question about it, or a message that is only close, is answered as usual with a button for this flow.',
  'flows.builder.intent.modeHint.offer': 'The message is answered as usual and this flow goes along as a button. It never starts on its own.',
  'flows.builder.intent.modeHint.off': 'Only the keywords above start this flow.',
  'flows.builder.intent.button': 'Button text',
  'flows.builder.intent.buttonHint': 'Up to {{max}} characters. Left empty, the flow\'s name is used.',
  'flows.builder.intent.try': 'Try a message',
  'flows.builder.intent.tryHint': 'Type what a customer might send to see what would happen. It checks your flows as they were last saved and switched on. Nothing is sent.',
  'flows.builder.intent.tryPlaceholder': 'I want to register for the event',
  'flows.builder.intent.tryRun': 'Try',
  'flows.builder.intent.result.jump': 'This would start “{{flow}}” ({{score}}% match).',
  'flows.builder.intent.result.offer': 'This would be answered as usual, with a “{{label}}” button for “{{flow}}” ({{score}}% match).',
  'flows.builder.intent.result.keyword': 'The keyword flow “{{flow}}” would start first.',
  'flows.builder.intent.result.none': 'No flow would be started or offered.',
  'flows.builder.intent.reason.question': 'It reads as a question, so it gets an answer and a button, never a form.',
  'flows.builder.intent.reason.close_call': 'Two flows are almost equally close, so both are offered.',
  'flows.builder.intent.reason.offer_only': 'The flow is set to be offered as a button only.',
  'flows.builder.intent.reason.likely': 'It is close, but not clear enough to start on its own.',
  'flows.builder.intent.reason.no_match': 'The closest is “{{flow}}” at {{score}}%.',
  'flows.builder.intent.reason.no_flows': 'No flow that is switched on says what it is for yet. Save this one and switch it on first.',
  'flows.builder.intent.reason.greeting': 'A hello is greeted, not matched.',
  'flows.builder.intent.reason.small_talk': 'Thanks, ok and goodbye are answered as small talk.',
  'flows.builder.intent.reason.too_short': 'One word is too little to go on. Keywords are for that.',
  'flows.builder.intent.reason.too_long': 'Very long messages are not matched.',
  'flows.builder.intent.reason.unavailable': 'Matching is not available right now. Try again in a moment.',
  'flows.builder.summary.orAsked': 'or when asked for',
  'flows.builder.summary.asked': 'When a customer asks for it',
  'flows.intentBadge.jump': 'Starts when a customer asks for it',
  'flows.intentBadge.offer': 'Offered as a button when asked about',
  'flows.view.runs.startedBy.keyword': 'Started by a keyword in the message.',
  'flows.view.runs.startedBy.intent': 'Started because the customer asked for it ({{score}}% match).',
  'flows.view.runs.startedBy.first_contact': 'Started by the first message from a new person.',
  'flows.view.runs.startedBy.greeting': 'Started by a hello.',
  'flows.view.runs.startedBy.default': 'Started because no other flow claimed the message.',
  'flows.view.runs.startedBy.offer': 'Started because the customer tapped the button the assistant offered.',
  'flows.view.runs.startedBy.switch': 'Started because the customer asked for it in the middle of another flow ({{score}}% match).',
  'flows.view.runs.startedBy.api': 'Started from your own system.',
  'flows.view.runs.startedBy.test': 'Started in the test chat.',
  'flows.intentBadge.short.jump': 'Starts when asked for',
  'flows.intentBadge.short.offer': 'Offered as a button',
  'flows.builder.field.useLiveSources': 'Use live sources',
  'flows.builder.field.useLiveSourcesHint': 'When the question is one a live source answers, your system is asked and its answer is used first.',
  'flows.builder.field.authSecretSaved': 'Saved. Leave empty to keep it.',
  'flows.assistant.keptNotice': 'We keep these chats for 30 days to improve the assistant, then delete them.',
  'flows.builder.node.sms': 'Send an SMS',
  'flows.builder.node.smsHint': 'A text message to the customer\'s phone',
  'flows.node.sms': 'Send SMS',
  'flows.builder.picker.long.sms': 'Texts the customer by SMS, on their own number unless you give another, such as a number they typed in earlier. Each SMS part is charged from your credits, and a customer who has opted out of SMS gets nothing. When you test the flow, the SMS is shown, not sent. Whether it went out is kept in {{sms.ok}}.',
  'flows.builder.picker.when.sms': 'A booking reference, a code or a link the customer should keep, or reaching them once they have left the chat.',
  'flows.builder.summary.smsNone': 'No text yet',
  'flows.validate.smsNoText': 'The SMS step has no text. Type the message to send.',
  'flows.builder.field.smsIntro': 'Texts the customer, not your team (to tell your team, use Notify your team). Each SMS part is charged from your credits. A customer who has opted out of SMS is skipped, and the flow carries on either way.',
  'flows.builder.field.smsMessage': 'Message',
  'flows.builder.field.smsTo': 'To',
  'flows.builder.field.smsToHint': 'Leave blank to text the customer\'s own number. To use another, put in a number collected earlier, such as {{phone}}. A website chat has no number, so ask for one first.',
  'flows.builder.field.smsPartsOne': '1 SMS part',
  'flows.builder.field.smsPartsMany': '{{n}} SMS parts, charged per part',
  'flows.builder.field.smsPartsTooMany': '{{n}} SMS parts, which is too long. The most is {{max}}, so shorten the message.',
  'flows.builder.field.smsPartsTokens': 'Counted as typed: the names and answers filled in can change it.',
  'flows.builder.field.smsUnavailable': 'SMS is not available yet. Until it is, this step sends nothing and the flow carries on to the next step.',
  'flows.builder.node.email': 'Send an email',
  'flows.builder.node.emailHint': 'An email to the customer',
  'flows.node.email': 'Send email',
  'flows.builder.picker.long.email': 'Emails the customer, at the address on their contact unless you give another, such as an address they typed in earlier. Each email is charged from your credits. A customer with no email address, or who has opted out of email, gets nothing and the flow carries on. When you test the flow, the email is shown, not sent. Whether it went out is kept in {{email.ok}}.',
  'flows.builder.picker.when.email': 'A booking confirmation, a quote or a receipt the customer should keep, or details too long for a chat message.',
  'flows.builder.summary.emailNone': 'No subject or text yet',
  'flows.validate.emailNoSubject': 'The email step has no subject. Type one in.',
  'flows.validate.emailNoText': 'The email step has no text. Type the message to send.',
  'flows.builder.field.emailIntro': 'Emails the customer, not your team (to tell your team, use Notify your team). Each email is charged from your credits. The flow carries on either way.',
  'flows.builder.field.emailSubject': 'Subject',
  'flows.builder.field.emailSubjectHint': 'What the customer sees before opening the email.',
  'flows.builder.field.emailMessage': 'Message',
  'flows.builder.field.emailMessageHint': 'Plain text. A blank line starts a new paragraph.',
  'flows.builder.field.emailTo': 'To',
  'flows.builder.field.emailToHint': 'Leave blank to email the address on the customer\'s contact. To use another, put in an address collected earlier, such as {{email}}. Skipped when the customer has no email address, and a customer who opted out of email gets nothing. Charged per email.',
  'flows.builder.field.emailUnavailable': 'Email is not available yet. Until it is, this step sends nothing and the flow carries on to the next step.',
  'bots.channels.kind.ussd': 'USSD code',
  'flows.validate.ussd.template': '{{id}} does not work on USSD: a WhatsApp template cannot be sent over USSD.',
  'flows.validate.ussd.media': '{{id}} does not work on USSD: a picture, video or document cannot be shown over USSD.',
  'flows.validate.ussd.delay': '{{id}} does not work on USSD: a USSD session cannot wait for a delay, because the network closes it within a minute.',
  'flows.validate.ussd.handoff': '{{id}} does not work on USSD: a USSD session cannot be handed to your team, because nobody can answer it in time.',
  'flows.validate.ussd.llm.reply': '{{id}} does not work on USSD: an AI answer takes longer than a USSD screen waits. Sorting and pulling details out with the AI do work.',
  'flows.validate.ussd.llm.rewrite': '{{id}} does not work on USSD: AI rewording takes longer than a USSD screen waits.',
  'flows.validate.ussd.http.ai_reply': '{{id}} does not work on USSD: having the AI put a Web API answer into words takes longer than a USSD screen waits.',
  'flows.builder.field.contactLocale': 'Language',
  'flows.builder.field.contactLocaleHint': 'The contact\'s language: pick one, or type a variable such as {{language}}.',
  'flows.builder.field.confirmChanges': 'Ask the customer before changing details they already have',
  'flows.builder.field.confirmChangesHint': 'When a name, email address or language is already there, the customer is asked “We have your name as Stanley. Shall we change it to Stan?” and it changes only if they say yes. The old value is always kept on the contact.',
  'flows.builder.field.confirmChangeMessage': 'Your own question',
  'flows.builder.field.confirmChangeMessageHint': 'Optional. Use {{field}} for what is changing, {{old}} for what is there now and {{new}} for the new value. Left empty, the bot asks in its own language.',
  'flows.builder.field.confirmChangeMessagePlaceholder': 'We have your {{field}} as {{old}}. Shall we change it to {{new}}?',
  'flows.builder.summary.contactAsks': 'Asks before changing · {{parts}}',
  'flows.builder.field.hoursSource': 'Opening hours',
  'flows.builder.hoursSource.business': 'Use the business\'s opening hours',
  'flows.builder.hoursSource.custom': 'Set hours for this step',
  'flows.builder.field.hoursBusinessHint': 'Set them under Settings, Organisation.',
  'flows.builder.field.hoursTimezoneHint': 'An IANA name such as Africa/Johannesburg. Leave it empty to use the business\'s time zone.',
  'flows.builder.summary.hoursBusiness': 'Business opening hours',
  'flows.builder.node.booking': 'Book a time',
  'flows.builder.node.bookingHint': 'Free days and times from Bookings, then the booking',
  'flows.builder.node.manage_booking': 'Manage a booking',
  'flows.builder.node.manage_bookingHint': 'Confirm, move or cancel an upcoming booking',
  'flows.node.booking': 'Book a time',
  'flows.node.manage_booking': 'Manage a booking',
  'flows.builder.picker.long.booking': 'Takes a booking from your Bookings setup. The customer picks a day with free times, then a time (with more on request), answers the service\'s own questions and sees it all read back before saying Yes, book it. Appointments, classes and call-outs all work, and the confirmation, calendar file and reminders go out from Bookings.',
  'flows.builder.picker.when.booking': 'Whenever customers should book an appointment, a class or a visit in the chat. Make it the first step of a flow that starts when someone asks to book.',
  'flows.builder.picker.long.manage_booking': 'Finds the customer\'s upcoming bookings by their number or email address and lets them confirm one, move it to another free time or cancel it, within the change cut-off set under Bookings.',
  'flows.builder.picker.when.manage_booking': 'For "change my booking" and "cancel my appointment": a flow that starts when someone asks for it.',
  'flows.builder.port.booked': 'Booked',
  'flows.builder.port.requested': 'Requested',
  'flows.builder.port.full': 'Nothing free',
  'flows.builder.port.done': 'Done',
  'flows.builder.port.noBookings': 'No bookings',
  'flows.builder.port.tooLate': 'Too late',
  'flows.builder.summary.bookingChoose': 'The client chooses the service',
  'flows.builder.summary.bookingService': 'Books {{service}}',
  'flows.builder.summary.bookingSomeService': 'Books the chosen service',
  'flows.builder.summary.manageBooking': 'Confirm, move or cancel',
  'flows.validate.bookingNoService': 'Choose a service, or let the client choose.',
  'flows.validate.bookingDays': 'Days ahead must be a whole number from 1 to {{max}}.',
  'flows.validate.bookingAsk': 'The questions to ask must be a list of question keys.',
  'flows.builder.booking.intro': 'The customer picks a day, then a time, answers the service\'s questions and says yes to a summary. The booking is made in Bookings, which sends the confirmation and the reminders.',
  'flows.builder.booking.setupTitle': 'Set up bookings first',
  'flows.builder.booking.setupBody': 'This step offers the services, people and hours you set up under Bookings. There are none yet, or they could not be loaded.',
  'flows.builder.booking.setupLink': 'Go to Bookings, Services',
  'flows.builder.booking.service': 'Service',
  'flows.builder.booking.serviceHint': 'One service, or a list the client chooses from.',
  'flows.builder.booking.letClientChoose': 'Let the client choose',
  'flows.builder.booking.unknownService': 'A service that is no longer offered',
  'flows.builder.booking.staff': 'With whom',
  'flows.builder.booking.staffHint': 'For appointments. A class has its own teacher and a call-out goes to whoever is free.',
  'flows.builder.booking.staffAny': 'Anyone free',
  'flows.builder.booking.staffChoose': 'The client chooses',
  'flows.builder.booking.unknownStaff': 'Someone no longer on the team',
  'flows.builder.booking.daysAhead': 'Days ahead',
  'flows.builder.booking.daysAheadHint': 'How far ahead free days are offered, from 1 to {{max}}.',
  'flows.builder.booking.questions': 'Questions to ask',
  'flows.builder.booking.questionsHint': 'The service\'s own questions, set under Bookings, Services. Untick one to leave it out here.',
  'flows.builder.booking.noQuestions': 'This service has no questions of its own. The name is asked only when the contact has none.',
  'flows.builder.booking.questionsAll': 'All the questions of the service the client chooses are asked.',
  'flows.builder.booking.confirm': 'Read it back before booking',
  'flows.builder.booking.confirmHint': 'The customer sees the service, time and answers and taps Yes, book it or No.',
  'flows.builder.booking.exits': 'Ways out: Booked, Requested (the service needs your approval), Nothing free, and Cancelled (the customer said no). Afterwards {{booking.number}}, {{booking.when}}, {{booking.service}}, {{booking.staff}} and {{booking.status}} are filled in.',
  'flows.builder.manageBooking.intro': 'Lists the customer\'s upcoming bookings, found by their number or email address, and lets them confirm one, change the time or cancel it. A change inside the cut-off set under Bookings is refused with a polite request to call.',
  'flows.builder.manageBooking.exits': 'Ways out: Done, No bookings (nothing upcoming) and Too late (too close to the time to change it here).',
  'flows.builder.manageBooking.daysAheadHint': 'How far ahead a new time may be, from 1 to {{max}}.',
  'flows.builder.node.ticket': 'Open a ticket',
  'flows.builder.node.ticketHint': 'A ticket for your team to follow up',
  'flows.builder.node.ticket_status': 'Ticket status',
  'flows.builder.node.ticket_statusHint': 'Tell the client where their request stands',
  'flows.node.ticket': 'Open a ticket',
  'flows.node.ticket_status': 'Ticket status',
  'flows.builder.picker.long.ticket': 'Opens a ticket under Tickets for your team to work through: a call back, a question, a call-out. It is linked to this chat and the client, with a subject, a description and the ticket type\'s fields filled in from what the chat collected. Tickets can tell the client their request number themselves.',
  'flows.builder.picker.when.ticket': 'Whenever a request needs your team to follow up. Ask what is needed first (and an address for a call-out), then open the ticket.',
  'flows.builder.picker.long.ticket_status': 'Tells the client where their open requests stand. One request is answered at once; several are offered as a list; with none, it asks for the request number and looks it up, showing only the client\'s own.',
  'flows.builder.picker.when.ticket_status': 'For "where is my request" and "any news on my complaint": a flow that starts when someone asks for it.',
  'flows.builder.port.created': 'Created',
  'flows.builder.port.failed': 'Not created',
  'flows.builder.port.noTickets': 'Nothing open',
  'flows.builder.summary.ticket': 'Opens a ticket',
  'flows.builder.summary.ticketType': 'Opens a {{type}} ticket',
  'flows.builder.summary.ticketStatus': 'Where the client\'s requests stand',
  'flows.validate.ticketNotText': 'The ticket step\'s {{field}} must be text.',
  'flows.validate.ticketFields': 'Each field value needs a field key and text.',
  'flows.validate.ticketTags': 'Tags must be a list of words.',
  'flows.validate.ticketNotify': 'Telling the client must be on or off.',
  'flows.builder.ticket.intro': 'Opens a ticket under Tickets, linked to this chat and the client. Nothing is said to the client by this step itself.',
  'flows.builder.ticket.setupTitle': 'Set up tickets first',
  'flows.builder.ticket.setupBody': 'This step uses the ticket types, priorities and teams you set up under Tickets. They could not be loaded.',
  'flows.builder.ticket.setupLink': 'Go to Tickets, Settings',
  'flows.builder.ticket.type': 'Ticket type',
  'flows.builder.ticket.typeHint': 'The type decides the fields to fill in and its default team and priority.',
  'flows.builder.ticket.typeDefault': 'The default type',
  'flows.builder.ticket.unknownType': 'A type that is no longer in use',
  'flows.builder.ticket.priority': 'Priority',
  'flows.builder.ticket.priorityHint': 'Left as the default, the type\'s own priority applies.',
  'flows.builder.ticket.priorityDefault': 'The type\'s default',
  'flows.builder.ticket.unknownPriority': 'A priority that no longer exists',
  'flows.builder.ticket.team': 'Team',
  'flows.builder.ticket.teamHint': 'Left as the default, the type\'s own team, or your rules, decide.',
  'flows.builder.ticket.teamDefault': 'The type\'s default',
  'flows.builder.ticket.unknownTeam': 'A team that no longer exists',
  'flows.builder.ticket.subject': 'Subject',
  'flows.builder.ticket.subjectHint': 'What your team sees in the queue. Left empty, it is the client\'s name and their last message.',
  'flows.builder.ticket.description': 'Description',
  'flows.builder.ticket.descriptionHint': 'Optional. The details for whoever picks it up.',
  'flows.builder.ticket.fields': 'The type\'s fields',
  'flows.builder.ticket.fieldRequired': 'The type requires it.',
  'flows.builder.ticket.fieldOptional': 'Optional; leave it empty for your team to fill in.',
  'flows.builder.ticket.fieldOptions': 'One of: {{options}}.',
  'flows.builder.ticket.noFields': 'This type has no fields of its own.',
  'flows.builder.ticket.otherFields': 'Field values',
  'flows.builder.ticket.otherFieldsHint': 'A field key of the ticket type and the value to put in it. Choose a type above to see its fields by name.',
  'flows.builder.ticket.fieldKey': 'Field key',
  'flows.builder.ticket.tags': 'Tags',
  'flows.builder.ticket.tagsHint': 'Separate tags with commas.',
  'flows.builder.ticket.notify': 'Tell the client',
  'flows.builder.ticket.notifyHint': 'Tickets sends the client their request number. Switch it off to say it in your own words with {{ticket.number}}.',
  'flows.builder.ticket.exits': 'Ways out: Created and Not created (Tickets could not be reached or refused it). Afterwards {{ticket.number}}, {{ticket.id}} and {{ticket.status}} are filled in. In the test chat no ticket is made: the step carries on as Created with the number T-TEST.',
  'flows.builder.ticketStatus.intro': 'Finds the client\'s open requests. One is answered at once with where it stands; several are offered as a list to choose from; with none, the client is asked for a request number, which is looked up among their own requests only.',
  'flows.builder.ticketStatus.exits': 'Ways out: Answered, and Nothing open (no open request, or the number was not found). Afterwards {{ticket.number}} and {{ticket.status}} are filled in.',
  'flows.ws.tabs.entries': 'Entry points',
  'flows.ws.tabs.channels': 'Where it answers',
  'flows.ws.tabs.questions': 'Unanswered questions',
  'flows.ws.tabs.attention': '{{count}} need your attention',
  'flows.builder.field.alwaysAsk': 'Ask even if we already know',
  'flows.builder.field.alwaysAskHint': 'A question for a name, an email address or a phone number is skipped when the contact book or the channel already gives the answer: Messenger and Instagram give the name, WhatsApp the profile name and the number. Switch this on to ask anyway.',
  'flows.ws.tabs.coach': 'Coach',
  'flows.coach.title': 'Coach',
  'flows.coach.description': 'The coach reads the chatbot\'s recent conversations and suggests fixes: a missing answer, clearer wording, a better greeting. Nothing changes until you press Apply. Each review is charged as AI use.',
  'flows.coach.reviewNow': 'Review now',
  'flows.coach.reviewing': 'Reviewing…',
  'flows.coach.started': 'The review has started. Suggestions appear here when it is done.',
  'flows.coach.nothing': 'There are no new conversations to review yet.',
  'flows.coach.confirmTitle': 'Review the chatbot\'s conversations?',
  'flows.coach.confirmBody': 'Conversations to read: {{count}}, since {{since}}.',
  'flows.coach.cost': 'This review costs about {{amount}}.',
  'flows.coach.costUnknown': 'The cost could not be worked out right now. It is charged as AI use at your plan\'s rate.',
  'flows.coach.notEnough': 'There are not enough credits for this review. Top up under Billing, or lower the number of conversations a review reads.',
  'flows.coach.run': 'Start review',
  'flows.coach.status.queued': 'Waiting to start',
  'flows.coach.status.running': 'Reviewing',
  'flows.coach.status.done': 'Last review',
  'flows.coach.status.failed': 'The last review did not finish',
  'flows.coach.status.skipped': 'The last scheduled review was skipped',
  'flows.coach.found': 'conversations read: {{conversations}}, suggestions: {{suggestions}}',
  'flows.coach.scheduled': 'on schedule',
  'flows.coach.scheduleTitle': 'Review on a schedule',
  'flows.coach.schedule': 'Review',
  'flows.coach.schedule.off': 'Only when I ask',
  'flows.coach.schedule.daily': 'Every day',
  'flows.coach.schedule.weekly': 'Every week',
  'flows.coach.weekday': 'Day',
  'flows.coach.time': 'Time',
  'flows.coach.max': 'Conversations per review',
  'flows.coach.maxHint': '5 to 100. Fewer conversations cost less.',
  'flows.coach.offHint': 'The coach reviews only when you press Review now.',
  'flows.coach.scheduleHint': 'A scheduled review reads only conversations since the last one, and is skipped when there is nothing new or not enough credit.',
  'flows.coach.next': 'Next review: {{when}}.',
  'flows.coach.saved': 'Schedule saved.',
  'flows.coach.day.0': 'Sunday',
  'flows.coach.day.1': 'Monday',
  'flows.coach.day.2': 'Tuesday',
  'flows.coach.day.3': 'Wednesday',
  'flows.coach.day.4': 'Thursday',
  'flows.coach.day.5': 'Friday',
  'flows.coach.day.6': 'Saturday',
  'flows.coach.suggestions': 'Suggestions',
  'flows.coach.noSuggestions': 'No suggestions waiting',
  'flows.coach.noSuggestionsHint': 'Run a review, or set a schedule, and the coach\'s suggestions appear here for you to apply or dismiss.',
  'flows.coach.kind.correction': 'Knowledge',
  'flows.coach.kind.flow_wording': 'Flow wording',
  'flows.coach.kind.persona': 'Chatbot',
  'flows.coach.kind.note': 'For your team',
  'flows.coach.openConversation': 'Open the conversation',
  'flows.coach.correctionTitle': 'Subject',
  'flows.coach.correctionAnswer': 'Answer',
  'flows.coach.correctionHint': 'Fill in anything in square brackets before you apply. It becomes a correction the chatbot puts ahead of every document.',
  'flows.coach.before': 'Now',
  'flows.coach.after': 'Change to',
  'flows.coach.openFlow': 'Open the flow',
  'flows.coach.persona.business_description': 'What the business does',
  'flows.coach.persona.greeting': 'Greeting',
  'flows.coach.persona.tone': 'Tone',
  'flows.coach.empty': '(empty)',
  'flows.coach.tone.friendly': 'Friendly',
  'flows.coach.tone.formal': 'Formal',
  'flows.coach.tone.playful': 'Playful',
  'flows.coach.apply': 'Apply',
  'flows.coach.markDone': 'Mark as done',
  'flows.coach.dismiss': 'Dismiss',
  'flows.coach.applied': 'Applied.',
  'flows.coach.dismissed': 'Dismissed.',
  'flows.view.runs.startedBy.post_resolution': 'Started after your team resolved the chat.',
  'flows.builder.intent.result.choose': 'This matches the keywords of {{flows}}.',
  'flows.builder.intent.result.chooseClose': 'This is close to the keyword of {{flows}}, perhaps with a spelling mistake.',
  'flows.builder.intent.result.chooseAssistant': 'The assistant would choose using what each flow is for and the conversation, and ask the customer if it can\'t tell.',
  'flows.builder.intent.result.chooseCloseAssistant': 'The assistant would check it against what the flow is for and the conversation, start it if that fits, and ask the customer if it can\'t tell.',
  'flows.builder.intent.result.chooseAsk': 'The customer would be asked which one they mean.',
  'flows.builder.intent.suggest': 'Suggest more',
  'flows.builder.intent.suggestHint': 'Uses a little AI credit. You choose which to add.',
  'flows.builder.intent.suggestNeeds': 'Say what this flow is for, or add a keyword, to get suggestions.',
  'flows.builder.intent.suggestNone': 'No new suggestions this time. Try again, or add a few of your own.',
  'flows.builder.intent.addPhrase': 'Add “{{phrase}}”',
  'flows.builder.intent.addAll': 'Add all',
  'flows.builder.intent.dismiss': 'Dismiss',
  'flows.builder.keywordClash.same': '“{{keyword}}” also starts “{{flow}}”.',
  'flows.builder.keywordClash.overlap': '“{{keyword}}” overlaps with “{{other}}”, which starts “{{flow}}”.',
  'flows.builder.keywordClash.close': '“{{keyword}}” is very close to “{{other}}”, which starts “{{flow}}”.',
  'flows.builder.keywordClash.open': 'Open',
  'flows.builder.keywordClash.how': 'When a message matches more than one, the assistant chooses using what each flow is for and the conversation, and asks the customer when it can\'t tell. You can change this under Settings, Your assistant.',
  'flows.builder.keywordClash.tip': 'Fill in “When a customer asks for it” on each of these flows: one clear sentence helps the assistant choose.',
  'flows.view.runs.startedBy.chosen': 'Started because the message matched more than one flow, and the assistant chose this one.',
  'flows.view.runs.startedBy.choice': 'Started because the customer picked this flow when asked which one they meant.',
  'flows.assistant.slow': 'A bigger flow can take up to a minute to write. Keep this open.',
  'flows.assistant.timeout': 'That took too long, so nothing was changed. Try again, or ask for one part of the flow at a time.',
  'flows.builder.field.followUps': 'Keep answering follow-up questions',
  'flows.builder.field.followUpsHint': 'After its answer the step waits: anything the customer asks next, typed or tapped from the suggestions, is answered here too. The flow moves on when they tap the button below. Each answer uses AI credit.',
  'flows.builder.field.moveOnLabel': 'Button to move on',
  'flows.builder.field.moveOnLabelHint': 'Up to 20 characters. Leave empty for “Nothing else”.',
  'flows.builder.field.moveOnLabelPlaceholder': 'Nothing else',
  'flows.assistant.model': 'Model',
  'flows.assistant.modelStandard': 'Standard ({{name}})',
  'flows.assistant.modelStronger': 'Stronger ({{name}})',
  'flows.assistant.modelStandardHint': 'Quick and cheapest. Choose the stronger model for a big flow or a tricky change.',
  'flows.assistant.modelCost': 'Better with big flows and tricky changes, but each message uses about {{factor}} times as much AI credit.',
  'flows.assistant.modelCostUnknown': 'Better with big flows and tricky changes, but each message uses more AI credit.',
  'flows.validate.httpSecretInHeader': 'The header {{header}} holds a secret that anyone who opens this flow can read. Move it to Authentication (API key) above, where it is kept sealed once saved.',
  'flows.assistant.putOnCanvas': 'Put on canvas',
  'flows.assistant.putAgain': 'Put this version on the canvas',
  'flows.builder.node.lookup': 'Look up',
  'flows.builder.node.lookupHint': 'Read one of your live sources into variables',
  'flows.node.lookup': 'Look up',
  'flows.builder.picker.long.lookup': 'Reads one of your live sources now, with details from the flow such as a date the customer gave, and keeps what it read as variables: each data point, the whole answer as text, and how many items came back. It says nothing itself. Leaves by Found, Not found or Error.',
  'flows.builder.picker.when.lookup': 'Before a message or a decision that needs your system\'s data, such as the free tour times on a day or an order\'s status.',
  'flows.builder.port.notFound': 'Not found',
  'flows.builder.summary.lookup': 'Looks up {{source}}',
  'flows.builder.summary.lookupNone': 'Choose a live source',
  'flows.validate.lookupNoSource': 'Choose the live source to look up.',
  'flows.validate.httpConnectionPath': 'On an API connection, the address is a path that starts with /.',
  'flows.builder.lookup.source': 'Live source',
  'flows.builder.lookup.sourceHint': 'Live sources are added on the Knowledge base page.',
  'flows.builder.lookup.noSources': 'There are no live sources yet. Add one on the Knowledge base page, from your API\'s documentation or by hand.',
  'flows.builder.lookup.sourcePick': 'Choose a live source',
  'flows.builder.lookup.sourceGone': 'A removed live source',
  'flows.builder.lookup.optional': '(optional)',
  'flows.builder.lookup.saveAs': 'Keep it as',
  'flows.builder.lookup.saveAsHint': 'Later steps read it as {{example}}.',
  'flows.builder.lookup.variables': 'Variables it adds',
  'flows.builder.lookup.exits': 'Found when something came back, Not found when nothing did (or your system said it does not exist), Error when the call failed.',
  'flows.builder.http.connection': 'API connection',
  'flows.builder.http.connectionHint': 'Use a connection from the Knowledge base page, so the address and sign-in are kept in one place.',
  'flows.builder.http.connectionOnHint': 'The address is a path on {{base}}, and the sign-in is the connection\'s.',
  'flows.builder.http.connectionOwn': 'None: its own address and sign-in',
  'flows.builder.http.connectionGone': 'A removed connection',
  'flows.builder.http.action': 'Start from an action',
  'flows.builder.http.actionHint': 'Fills in the method, path and body from the API\'s documentation.',
  'flows.builder.http.actionPick': 'Choose an action',
  'flows.builder.http.path': 'Path',
  'flows.builder.http.signsIn': 'Signs in through {{name}}. Change its key on the Knowledge base page under API connections.',
  'flows.calls.title': 'API calls',
  'flows.calls.intro': 'Every call your flows made to other systems in the last 48 hours: what was sent and what came back. Open one to see its headers and bodies.',
  'flows.calls.kept': 'Kept for 48 hours. Keys and passwords are hidden.',
  'flows.calls.gone': 'This call is no longer kept: calls are deleted after 48 hours.',
  'flows.calls.noAnswer': 'No answer',
  'flows.calls.fromCache': 'From the cache',
  'flows.calls.took': '{{ms}} ms',
  'flows.calls.request': 'Request',
  'flows.calls.response': 'Response',
  'flows.calls.headers': 'Headers',
  'flows.calls.noHeaders': 'No headers.',
  'flows.calls.body': 'Body',
  'flows.calls.noBody': 'No body.',
  'flows.calls.copy': 'Copy',
  'flows.calls.copied': 'Copied',
  'flows.calls.drawerTitle': 'API call',
  'flows.calls.open': 'Request',
  'flows.calls.flow': 'Flow',
  'flows.calls.allFlows': 'All flows',
  'flows.calls.failedOnly': 'Failed calls only',
  'flows.calls.empty': 'No API calls in the last 48 hours',
  'flows.calls.emptyHint': 'Calls made by HTTP request, Look up and Notify steps show here.',
  'flows.calls.newer': 'Newer',
  'flows.calls.older': 'Older',
  'flows.builder.cache.keep': 'Keep the answer for',
  'flows.builder.cache.keepHint': 'A repeat of the same request within this time is answered from the cache, without asking the other system again.',
  'flows.builder.cache.writes': 'Only a request that reads can be kept. This one changes something, so it is always sent.',
  'flows.builder.cache.none': 'Do not keep it',
  'flows.builder.cache.m1': '1 minute',
  'flows.builder.cache.m5': '5 minutes',
  'flows.builder.cache.m15': '15 minutes',
  'flows.builder.cache.m60': '1 hour',
  'flows.builder.cache.m360': '6 hours',
  'flows.builder.cache.m1440': '1 day',
  'flows.builder.cache.share': 'Shared with',
  'flows.builder.cache.everyone': 'Every customer making the same request',
  'flows.builder.cache.conversation': 'This conversation only',
  'flows.builder.cache.read': 'This request only reads',
  'flows.builder.cache.readHint': 'Say so for a POST that searches or quotes and changes nothing; only then is its answer kept.',
  'flows.builder.list.use': 'Offer items from a list',
  'flows.builder.list.useHint': 'Builds the menu from a list, such as the slots an API sent back. Long lists get a More row by themselves.',
  'flows.builder.list.from': 'List',
  'flows.builder.list.fromHint': 'The variable that holds the list, such as {{tour_slots.items}}.',
  'flows.builder.list.title': 'What each row shows',
  'flows.builder.list.titleHint': 'Use {{item.…}} for the item\'s fields.',
  'flows.builder.list.value': 'What is kept when chosen',
  'flows.builder.list.valueHint': 'Usually the item\'s id, for the next step to send on. Left empty, it is the item\'s id.',
  'flows.builder.list.filter': 'Only offer items where',
  'flows.builder.list.filterField': 'The item\'s field',
  'flows.builder.list.filterOp': 'Compared',
  'flows.builder.list.filterValue': 'Value',
  'flows.builder.list.filterHint': 'For example item.date on {{preferred_date}}, to offer only that day\'s slots. Leave the field empty to offer every item.',
  'flows.builder.list.kept': 'The choice leaves by Chosen and keeps {{{{variable}}}}, {{{{variable}}_label}}, {{{{variable}}_item}} (the whole item) and {{{{variable}}_index}}. Up to {{max}} options of your own, such as Cancel, follow the items.',
  'flows.builder.list.op.eq': 'is',
  'flows.builder.list.op.neq': 'is not',
  'flows.builder.list.op.contains': 'contains',
  'flows.builder.list.op.starts_with': 'starts with',
  'flows.builder.list.op.on_date': 'is on the date',
  'flows.builder.list.op.after': 'is after',
  'flows.builder.list.op.before': 'is before',
  'flows.builder.list.op.gte': 'is at least',
  'flows.builder.list.op.lte': 'is at most',
  'flows.builder.list.op.filled': 'is filled in',
  'flows.builder.field.understand': 'Understand typed replies',
  'flows.builder.field.understandHint': 'When a customer types instead of tapping, the reply is read by meaning: "I want to book" picks Book a Tour, a question is answered from your knowledge base and asked again, and a date is read however it is written. Uses a little AI credit when the words alone are not enough.',
  'flows.builder.port.chosen': 'Chosen',
  'flows.builder.port.empty': 'Empty',
  'flows.builder.summary.askList': 'Choose from {{list}}',
  'flows.validate.listTooManyOwn': 'A menu built from a list can add at most {{max}} options of its own.',
  'flows.builder.validate.datetime': 'A date and a time',
  'flows.builder.validateDesc.datetime': '2 November at 9:00, 02/11/2026 09:00. Stored as 2026-11-02 09:00, with {{x_date}} and {{x_time}} too.',
  'flows.builder.retryDefault.datetime': 'Please send a date and a time, for example 2 November at 9:00.',
  'flows.builder.field.skipButton': 'Skip button on optional questions',
  'flows.builder.field.skipButtonHint': 'An optional question without choices shows a Skip button, so the customer can tap it instead of typing skip. Not shown on USSD or SMS, where typing skip still works.',
  'flows.coach.deepReview': 'Deep review',
  'flows.coach.deepConfirmTitle': 'Run a deep review?',
  'flows.coach.deepAbout': 'Every chatbot reply in these conversations is checked against your knowledge base and your flows: wrong answers, answers to something not asked, questions it missed, and flows started by the wrong keyword.',
  'flows.coach.deepJudge': 'Claude Sonnet judges each reply.',
  'flows.coach.deepJudgeFallback': 'Claude Sonnet is switched off, so Claude Haiku judges each reply instead.',
  'flows.coach.runDeep': 'Start deep review',
  'flows.coach.startedDeep': 'The deep review has started. Its findings appear here when it is done.',
  'flows.coach.mode.quick': 'Review',
  'flows.coach.mode.deep': 'Deep review',
  'flows.coach.counts.sentence': 'Reviewed {{conversations}} and {{replies}}: {{list}}.',
  'flows.coach.counts.conversations.one': '1 conversation',
  'flows.coach.counts.conversations.other': '{{count}} conversations',
  'flows.coach.counts.replies.one': '1 reply',
  'flows.coach.counts.replies.other': '{{count}} replies',
  'flows.coach.counts.ok': '{{count}} right',
  'flows.coach.counts.wrong': '{{count}} wrong',
  'flows.coach.counts.assumed': '{{count}} answered something not asked',
  'flows.coach.counts.missed': '{{count}} missed an answer the knowledge base has',
  'flows.coach.counts.misrouted': '{{count}} sent to the wrong flow',
  'flows.coach.counts.not_known': '{{count}} not in the knowledge base',
  'flows.coach.counts.stuck': '{{count}} left the customer stuck',
  'flows.coach.verdict.ok': 'Right',
  'flows.coach.verdict.wrong': 'Wrong answer',
  'flows.coach.verdict.assumed': 'Answered something not asked',
  'flows.coach.verdict.missed': 'Missed an answer',
  'flows.coach.verdict.misrouted': 'Wrong flow',
  'flows.coach.verdict.not_known': 'Not in the knowledge base',
  'flows.coach.verdict.stuck': 'Customer stuck',
  'flows.coach.filter': 'Show suggestions',
  'flows.coach.filter.all': 'All ({{count}})',
  'flows.coach.filter.count': '{{label}} ({{count}})',
  'flows.coach.customerSaid': 'The customer said',
  'flows.coach.botSaid': 'The chatbot said',
  'flows.coach.rightAnswer': 'What is right',
  'flows.coach.sources': 'From: {{names}}',
  'flows.coach.kind.keywords': 'Keywords',
  'flows.coach.kind.document': 'Document',
  'flows.coach.keywords.flow': 'Flow: {{name}}',
  'flows.coach.keywords.now': 'Keywords now: {{keywords}}',
  'flows.coach.keywords.after': 'New keywords',
  'flows.coach.keywords.hint': 'Separate them with commas, up to {{max}}. The flow starts when a message contains one of them.',
  'flows.coach.keywords.tooMany': 'Keep it to {{max}} keywords of up to {{length}} characters each.',
  'flows.coach.document.name': 'Document: {{name}}',
  'flows.coach.document.hint': 'Open the document and fix or remove what is wrong, then mark this as done.',
  'flows.coach.openDocument': 'Open the document',
  'flows.coach.deepSchedule': 'Deep review',
  'flows.coach.deepScheduleHint': 'On the same day and at the same time as the review.',
  'flows.coach.deepMax': 'Conversations per deep review',
  'flows.coach.deepMaxHint': '1 to 100. A deep review judges every reply, so each conversation costs more than in a review.',
  'flows.coach.deepNext': 'Next deep review: {{when}}.',
  'flows.actions.suggestWithAi': 'Suggest flows with AI',
  'flows.limit.reached': 'Chatbot flows included in {{plan}}: {{count}}, and all of them are in use. Entry points do not count. Move up a plan, or remove a flow you no longer use.',
  'flows.allowance.used': '{{current}} of {{allowance}} flows used.',
  'flows.allowance.entryFree': 'Entry points (Welcome new customers, Returning customers, Anything else, After resolution) do not count.',
  'flows.allowance.entryFreeCount': 'Not counted now: {{count}}.',
  'flows.allowance.seePlans': 'See plans',
  'flows.translations.tab': 'Translations',
  'flows.translations.title': 'Translations of {{name}}',
  'flows.translations.titleShort': 'Translations',
  'flows.translations.intro': 'Your flow\'s messages, questions and button and list labels are translated automatically the first time a customer is answered in another language. Correct any of them here.',
  'flows.translations.loadError': 'The translations could not be loaded.',
  'flows.translations.oneLanguage': 'This flow\'s chatbot speaks one language, so there is nothing to translate. Offer more languages in its settings.',
  'flows.translations.openSettings': 'Open the chatbot\'s settings',
  'flows.translations.language': 'Language',
  'flows.translations.unsaved': 'Translations follow the saved flow. Save your changes to see their wording here.',
  'flows.translations.reviewNote': 'Answers are fine; have a native speaker read this wording before you rely on it.',
  'flows.translations.nothing': 'This flow has no wording to translate.',
  'flows.translations.count': '{{done}} of {{total}} translated',
  'flows.translations.staleCount': '{{count}} need a look',
  'flows.translations.translateNow': 'Translate now',
  'flows.translations.translated': '{{count}} pieces translated into {{language}}.',
  'flows.translations.translateFailed': 'Could not translate',
  'flows.translations.charge': 'Translating is charged as AI use at your plan\'s rates and shows in your ledger as “AI translation”.',
  'flows.translations.all.action': 'Translate all again',
  'flows.translations.all.title': 'Translate everything into {{language}} again?',
  'flows.translations.all.body': 'Every automatic translation into {{language}} is made again, and its read marks are cleared. Wording you corrected is kept. It is charged as AI use.',
  'flows.translations.all.confirm': 'Translate again',
  'flows.translations.path.body': 'Message',
  'flows.translations.path.question': 'Question',
  'flows.translations.path.message': 'Message',
  'flows.translations.path.caption': 'Caption',
  'flows.translations.path.header': 'Heading',
  'flows.translations.path.footer': 'Footer',
  'flows.translations.path.list_button_text': 'List button',
  'flows.translations.path.options_button': 'List button',
  'flows.translations.path.move_on_label': 'Move-on button',
  'flows.translations.path.intro': 'Introduction',
  'flows.translations.path.retry_message': 'Try-again message',
  'flows.translations.path.cancel_message': 'Cancel message',
  'flows.translations.path.confirm_message': 'Confirmation',
  'flows.translations.path.confirm_change_message': 'Change confirmation',
  'flows.translations.path.done_message': 'Done message',
  'flows.translations.path.restart_message': 'Start-again message',
  'flows.translations.path.list.button_text': 'List button',
  'flows.translations.path.choice': 'Choice {{n}}',
  'flows.translations.path.choiceDescription': 'Choice {{n}}: description',
  'flows.translations.path.button': 'Button {{n}}',
  'flows.translations.path.buttonDescription': 'Button {{n}}: description',
  'flows.translations.path.row': 'List row {{n}}',
  'flows.translations.path.rowDescription': 'List row {{n}}: description',
  'flows.translations.source.machine': 'Automatic',
  'flows.translations.source.edited': 'Yours',
  'flows.translations.source.missing': 'Not translated yet',
  'flows.translations.needsLook': 'Needs a look',
  'flows.translations.readBadge': 'Read',
  'flows.translations.original': 'Original',
  'flows.translations.in': 'In {{language}}',
  'flows.translations.missingPlaceholder': 'Translated the first time a customer needs it, or type your own.',
  'flows.translations.counter': '{{count}} of {{max}} characters.',
  'flows.translations.tooLong': 'At most {{max}} characters here.',
  'flows.translations.lostParts': 'Keep {{parts}} exactly as the original has it.',
  'flows.translations.extraParts': 'The original has no {{parts}}; take it out.',
  'flows.translations.saved': 'Translation saved.',
  'flows.translations.undo.action': 'Use an automatic translation',
  'flows.translations.undo.title': 'Take your wording back?',
  'flows.translations.undo.body': 'Your wording in {{language}} is removed, and the next customer who needs it gets a fresh automatic translation.',
  'flows.translations.undo.confirm': 'Take it back',
  'flows.translations.markRead': 'Mark as read',
  'flows.translations.markUnread': 'Mark as not read',
  'flows.looseEnds.title': 'Loose ends',
  'flows.looseEnds.intro': 'Steps in your switched-on flows that lead nowhere, and flows that never start.',
  'flows.looseEnds.bar': '{{count}} loose ends in your flows.',
  'flows.looseEnds.barOne': '1 loose end in your flows.',
  'flows.looseEnds.barBreaking': 'Chats stop at {{count}} of them.',
  'flows.looseEnds.review': 'Review',
  'flows.looseEnds.empty': 'No loose ends: every step leads somewhere.',
  'flows.looseEnds.loadError': 'The loose ends could not be checked. This is not the same as having none.',
  'flows.looseEnds.breaks': 'Chats stop here',
  'flows.looseEnds.wholeFlow': 'The whole flow',
  'flows.looseEnds.unnamedStep': 'A step with no name',
  'flows.looseEnds.anotherFlow': 'another flow',
  'flows.looseEnds.kind.opens_missing': 'Opens a flow that has been deleted.',
  'flows.looseEnds.kind.opens_none': 'Runs another flow, but no flow is chosen.',
  'flows.looseEnds.kind.opens_off': 'Opens {{target}}, which is switched off.',
  'flows.looseEnds.kind.opens_old_version': 'Opens {{target}}, the old version. Its improved version is the one switched on.',
  'flows.looseEnds.kind.same_start': 'Never starts: {{target}} starts the same way and runs instead.',
  'flows.looseEnds.kind.button_nowhere': 'The button “{{button}}” leads nowhere.',
  'flows.looseEnds.kind.key_missing': 'Calls a web API, but its key is not saved.',
  'flows.looseEnds.kind.other': 'Leads nowhere.',
  'flows.looseEnds.fix.button': 'Fix',
  'flows.looseEnds.fix.relink': 'Point it at {{name}}',
  'flows.looseEnds.fix.switchOff': 'Switch {{name}} off',
  'flows.looseEnds.fixAll': 'Fix all ({{count}})',
  'flows.looseEnds.fixAllConfirm': 'Fix all',
  'flows.looseEnds.fixAllHint': 'Fix all makes only the fixes that are certain. The others wait for your choice.',
  'flows.looseEnds.confirm.title': 'Check this fix',
  'flows.looseEnds.confirm.relink': 'Point “{{step}}” at {{name}}? Customers who reach this step get {{name}} from now on.',
  'flows.looseEnds.confirm.switchOff': 'Switch {{name}} off? It stops answering customers until you switch it on again.',
  'flows.looseEnds.confirm.allTitle': 'Fix the loose ends?',
  'flows.looseEnds.confirm.all': 'This makes the {{count}} fixes that are certain. The others stay in the list for you to choose.',
  'flows.looseEnds.fixed': 'Loose ends fixed: {{count}}.',
  'flows.looseEnds.fixedNone': 'Nothing needed fixing.',
  'flows.looseEnds.fixFailed': 'Could not fix',
  'flows.looseEnds.openStep': 'Open the step',
  'flows.looseEnds.openFlow': 'Open the flow',
  'flows.delete.checking': 'Checking which flows open it…',
  'flows.delete.usedTitle': 'Switched-on flows open this flow',
  'flows.delete.usedLine': '{{flow}}, step “{{step}}”',
  'flows.delete.usedLineUnnamed': '{{flow}}, a step with no name',
  'flows.delete.usedHint': 'Customers who reach these steps would be told it is not available. Point the steps at another flow first, or delete anyway.',
  'flows.delete.pointAt': 'Point those steps at',
  'flows.delete.pointAtHint': 'Those steps open this flow instead, so customers carry on.',
  'flows.delete.pointAtChoose': 'Choose a flow',
  'flows.delete.history': 'Its run history is deleted with it. Runs already under way finish. This cannot be undone.',
  'flows.delete.anyway': 'Delete anyway',
  'flows.delete.relinked': '{{name}} deleted. Its steps now open {{target}}.',
  'flows.switchOff.title': 'Switch {{name}} off?',
  'flows.switchOff.body': 'These switched-on flows open {{name}}:',
  'flows.switchOff.hint': 'Customers who reach these steps will be told it is not available. You can switch it on again at any time.',
  'flows.switchOff.confirm': 'Switch off',
  'flows.validate.flowMissing': '{{id}} opens a flow that has been deleted. Customers who reach it are told it is not available. Choose another flow.',
  'flows.validate.flowOff': '{{id}} opens {{name}}, which is switched off. Customers who reach it are told it is not available. Switch {{name}} on, or choose another flow.',
  'flows.validate.optionUnlinked': '{{id}}: the button “{{option}}” leads nowhere, so the chat ends for anyone who taps it.',
  'flows.builder.node.language': 'Change language',
  'flows.builder.node.languageHint': 'Let the customer choose the language to chat in',
  'flows.node.language': 'Change language',
  'flows.builder.picker.long.language': 'Lists the languages your chatbot speaks, each in its own name (Français, isiZulu), with a row for more languages when they do not all fit. The customer taps one or types its name in any spelling. The choice is saved on the contact, a short line confirms it in the new language, and every step after this one speaks that language.',
  'flows.builder.picker.when.language': 'Behind a button such as Language on your menu, or in a flow that starts when someone asks for another language. Link Changed back to the menu so it shows in the new language. The languages are the ones chosen under Settings, Your assistant, Languages.',
  'flows.builder.port.languageChanged': 'Changed',
  'flows.builder.port.languageKept': 'Not changed',
  'flows.builder.summary.language': 'Lets the customer choose a language',
  'flows.builder.language.intro': 'Lets the customer choose the language to chat in. It lists the languages the chatbot speaks, saves the choice on the contact, and every step after it speaks that language.',
  'flows.builder.language.offered': 'Languages offered',
  'flows.builder.language.offeredHint': 'Customers see each one in its own name, with the language of the chat first.',
  'flows.builder.language.change': 'Change the languages',
  'flows.builder.language.oneLanguage': 'This flow\'s chatbot speaks only {{language}}, so this step has nothing to offer and carries on by Changed. Choose more languages under Settings, Your assistant, Languages.',
  'flows.builder.language.chooseMore': 'Choose languages',
  'flows.builder.language.where': 'The step offers the languages the chatbot speaks, chosen under Settings, Your assistant, Languages.',
  'flows.builder.language.openSettings': 'Open the chatbot\'s settings',
  'flows.builder.language.question': 'Question',
  'flows.builder.language.questionHint': 'Leave empty to ask in the customer\'s language: "Which language would you like to chat in?" Your own question is translated like the flow\'s other wording.',
  'flows.builder.language.confirm': 'Say that the language changed',
  'flows.builder.language.confirmHint': 'Sends a short line in the new language, such as "Done. I\'ll chat with you in Français from now on."',
  'flows.builder.language.exits': 'Ways out: Changed (a language was chosen) and Not changed (none was chosen, or no valid choice after two tries). With no link from Not changed, the chat carries on by Changed. Afterwards {{chat_language.name}} (the language\'s own name) and {{chat_language.code}} are filled in.',
  'flows.builder.language.test': 'In the test chat nothing is saved on a contact, but the rest of the test speaks the language chosen.',
  'flows.validate.contactLanguageField': '{{id}} saves a field called {{field}}, which does not change the language the chatbot speaks. Use the Change language step instead.'
};

export default flows;
